import { defineStore } from 'pinia'
import { useSettingsStore } from './settings-store'
import { useAuthStore } from 'stores/auth-store'
import { supabase } from 'src/services/supabase'
import type { UserSettings } from 'stores/auth-store-types'
import type {
  DefenseSnapshot,
  GameStoreState,
  GameTransaction,
  HalfInning,
  LineupEntry,
  PitchTransaction,
  PlateAppearance,
  Position,
  RunnerAdvanceTransaction,
  RunnersOnBase,
  PlayTransaction,
  AdminTransaction,
  Team,
  PlateAppearanceNarrative,
  SequenceItem,
  Base,
  PlayOutcome,
  AdjustmentTransaction,
  AdjustmentScope,
  ScoutingTransaction,
} from './base-types'
import { db } from 'src/models/db'

export const useGameStore = defineStore('game', {
  state: (): GameStoreState => ({
    appContext: 'SCORER',
    teamHome: null,
    teamVisiting: null,
    activeTeam: null,
    homeLineup: [],
    visitingLineup: [],
    editingLineupEntry: null,
    activeGameSummary: {
      gameId: '',
      isSetupComplete: true,
      homeScoreByInning: [],
      visitingScoreByInning: [],
      currentInning: 1,
      currentHalfInning: 'top',
      currentBatterId: 1,
      currentPitcherId: 1,
    },
    transactions: [],
    plateAppearances: [],
    currentPAIndex: 0,
    editingPitchId: null,
  }),
  getters: {
    // --- NEW: THE FIREWALL GETTERS ---
    livePlateAppearances(state): PlateAppearance[] {
      return state.plateAppearances.filter((pa) => pa.context === 'SCORER')
    },
    liveTransactions(state): GameTransaction[] {
      return state.transactions.filter((t) => t.context === 'SCORER')
    },
    homeRuns(state): number {
      if (!state.activeGameSummary) return 0
      return state.activeGameSummary.homeScoreByInning.reduce((total, num) => total + num, 0)
    },
    visitorRuns(state): number {
      if (!state.activeGameSummary) return 0
      return state.activeGameSummary.visitingScoreByInning.reduce((total, num) => total + num, 0)
    },
    activeInningString(state): string {
      const summary = state.activeGameSummary
      if (!summary) return ''

      const half = summary.currentHalfInning === 'top' ? 'Top' : 'Bottom'
      const inn = summary.currentInning
      const j = inn % 10
      const k = inn % 100

      let suffix = 'th'
      if (j === 1 && k !== 11) suffix = 'st'
      else if (j === 2 && k !== 12) suffix = 'nd'
      else if (j === 3 && k !== 13) suffix = 'rd'

      return `${half} ${inn}${suffix}`
    },
    activeOffensiveLineup: (state): LineupEntry[] => {
      if (!state.activeGameSummary) return []
      return (
        (state.activeGameSummary.currentHalfInning === 'top'
          ? state.visitingLineup
          : state.homeLineup) || []
      )
    },
    activeDefensiveLineup: (state): LineupEntry[] => {
      if (!state.activeGameSummary) return []
      return (
        (state.activeGameSummary.currentHalfInning === 'top'
          ? state.homeLineup
          : state.visitingLineup) || []
      )
    },
    currentHits(): PitchTransaction[] {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA || !currentPA.batterId) return []
      return this.liveTransactions.filter(
        (t) =>
          t.eventType === 'PITCH' &&
          t.plateAppearanceId === currentPA.id &&
          t.batterId === currentPA.batterId &&
          t.hitLocationX != null &&
          t.hitLocationY != null,
      ) as PitchTransaction[]
    },
    currentPitchIsHit() {
      let returnVal = false
      const currentPA = this.viewedPlateAppearance
      if (!currentPA || !currentPA.batterId) return []
      const txns = this.liveTransactions.filter(
        (t) => t.plateAppearanceId === currentPA.id && t.eventType === 'PITCH',
      ) as PitchTransaction[]
      txns.sort((a, b) => a.sequence - b.sequence)
      if (txns.length > 0) {
        const outcome = txns[txns.length - 1]!.pitchOutcome ?? ''
        returnVal = outcome === 'X'
      }
      return returnVal
    },
    currentBatterEntry(): LineupEntry | undefined {
      const lineup = this.activeOffensiveLineup
      return lineup.find((entry) => entry.player.id === this.activeGameSummary?.currentBatterId)
    },

    currentDefence(): { position: number; uniformNumber: number; relX?: number; relY?: number }[] {
      const currentPA = this.viewedPlateAppearance
      const snapshot = currentPA?.defenseSnapshot || {}
      const lineup =
        this.activeGameSummary?.currentHalfInning === 'top' ? this.homeLineup : this.visitingLineup

      const allDef = lineup ? lineup.concat(lineup.flatMap((l) => l.substitutions || [])) : []

      return Object.entries(snapshot).map(([posStr, state]) => {
        const playerId = typeof state === 'number' ? state : state?.playerId
        const entry = allDef.find((e) => e.player.id === playerId)

        // 1. Create the base object with just the guaranteed properties
        const result: { position: number; uniformNumber: number; relX?: number; relY?: number } = {
          position: Number(posStr),
          uniformNumber: entry?.player.uniformNumber || 0,
        }

        // 2. Only attach the coordinate properties if they actually exist
        if (typeof state === 'object') {
          if (state.relX !== undefined) result.relX = state.relX
          if (state.relY !== undefined) result.relY = state.relY
        }

        return result
      })
    },
    currentDefenseSnapshot(state): DefenseSnapshot {
      const currentHalf = state.activeGameSummary?.currentHalfInning ?? 'top'

      // 1. Try to find the most recent plate appearance where this team was on defense
      const priorDefensivePAs = this.livePlateAppearances.filter(
        (pa) => pa.halfInning === currentHalf,
      )

      if (priorDefensivePAs.length > 0) {
        const lastPA = priorDefensivePAs[priorDefensivePAs.length - 1]

        // Carry the mid-inning (or previous inning) substitutions forward!
        if (lastPA && lastPA.defenseSnapshot && Object.keys(lastPA.defenseSnapshot).length > 0) {
          return { ...lastPA.defenseSnapshot }
        }
      }

      // 2. Fallback: If this is the very first time they are on the field, build from the lineup
      const lineup = currentHalf === 'top' ? state.homeLineup : state.visitingLineup
      const snapshot: DefenseSnapshot = {}

      if (lineup) {
        lineup.forEach((entry) => {
          if (entry.startingPosition >= 1 && entry.startingPosition <= 9) {
            if (entry.player.id)
              snapshot[entry.startingPosition as Position] = { playerId: entry.player.id }
          }
        })
      }
      return snapshot
    },

    // --- TRANSACTION TARGET GETTER ---
    // Safely returns the PlayTransaction linked to the current pitch being edited/viewed
    activePlay(state): PlayTransaction | undefined {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return undefined
      const currentTxns = this.liveTransactions.filter((t) => t.plateAppearanceId === currentPA.id)
      const activePitchId =
        state.editingPitchId || currentTxns.filter((t) => t.eventType === 'PITCH').pop()?.id
      if (!activePitchId) return undefined
      return currentTxns.find(
        (t) => t.eventType === 'PLAY' && String(t.linkedPitchId) === String(activePitchId),
      ) as PlayTransaction | undefined
    },

    getHitClassification: () => {
      return (
        relX: number | null | undefined,
        relY: number | null | undefined,
      ): { depth: string; zone: string } => {
        if (relX == null || relY == null) return { depth: '', zone: '' }

        const dist = Math.sqrt(relX * relX + relY * relY)

        let deg = Math.atan2(relY, relX) * (180 / Math.PI)
        if (deg < 0) deg += 360

        let depth = ''
        let zone = ''

        /*
        Infield Dirt Edge: 0.45
        Outfield Short Arc: 0.60
        Outfield Fence: 0.75
         */
        // --- OUTFIELD LOGIC ---
        if (dist >= 0.45) {
          // Outfield Depth
          if (dist < 0.6)
            depth = 'S' // Shallow Outfield (Texas Leaguer)
          else if (dist < 0.7)
            depth = 'M' // Medium Outfield (Standard Fly)
          else depth = 'D' // Deep Outfield (Warning Track/Wall)

          // Outfield Zone
          const outfieldZones = [
            { label: '7', angle_start: 0, angle_end: 250 },
            { label: '78', angle_start: 250, angle_end: 260 },
            { label: '8', angle_start: 260, angle_end: 280 },
            { label: '89', angle_start: 280, angle_end: 290 },
            { label: '9', angle_start: 290, angle_end: 360 },
          ]
          for (const z of outfieldZones) {
            if (deg >= z.angle_start && deg < z.angle_end) {
              zone = z.label
              break
            }
          }
          if (!zone) zone = '9'

          // --- INFIELD LOGIC ---
        } else {
          // Infield Depth
          if (dist < 0.15)
            depth = 'S' // Shallow Infield (Bunt / Chopper)
          else if (dist < 0.3)
            depth = 'M' // Medium Infield (Standard Grounder)
          else depth = 'D' // Deep Infield (In the hole / Edge of grass)

          // Infield Zone
          const infieldZones = [
            { label: '5', angle_start: 0, angle_end: 240 },
            { label: '56', angle_start: 240, angle_end: 245 },
            { label: '6', angle_start: 245, angle_end: 265 },
            { label: '64', angle_start: 265, angle_end: 275 },
            { label: '4', angle_start: 275, angle_end: 295 },
            { label: '43', angle_start: 295, angle_end: 300 },
            { label: '3', angle_start: 300, angle_end: 360 },
          ]
          for (const z of infieldZones) {
            if (deg >= z.angle_start && deg < z.angle_end) {
              zone = z.label
              break
            }
          }
          if (!zone) zone = '3'
        }

        return { depth, zone }
      }
    },

    viewedPlateAppearance(state): PlateAppearance | null {
      return this.livePlateAppearances[state.currentPAIndex] || null
    },

    currentInningString(state): string {
      const currentPA = this.viewedPlateAppearance
      if (!state.activeGameSummary) return ''
      const inning = currentPA!.inning ?? state.activeGameSummary.currentInning ?? 1
      let half: string = currentPA!.halfInning ?? state.activeGameSummary.currentHalfInning ?? 'top'
      half = half === 'top' ? 'Top' : 'Bot'
      const j = inning % 10
      const k = inning % 100
      let suffix = 'th'
      if (j === 1 && k !== 11) suffix = 'st'
      else if (j === 2 && k !== 12) suffix = 'nd'
      else if (j === 3 && k !== 13) suffix = 'rd'
      return `${half} ${inning}${suffix}`
    },

    currentBaseRunners(state): RunnersOnBase {
      const currentPA = this.viewedPlateAppearance
      const runnersByBase: RunnersOnBase = { '1': [], '2': [], '3': [], B: [] }
      if (!currentPA) return runnersByBase

      // Find the most recent inning/outs adjustment within this half-inning, if any —
      // anything before it is a known gap and must not be aggregated across.
      const inningAdjustments = this.liveTransactions.filter(
        (t): t is AdjustmentTransaction =>
          t.eventType === 'ADJUSTMENT' &&
          t.inning === currentPA.inning &&
          t.halfInning === currentPA.halfInning &&
          (t.scope === 'inning' || t.scope === 'outs'),
      )
      const lastAdjustment = inningAdjustments.sort((a, b) => b.sequence - a.sequence)[0]
      const cutoffSequence = lastAdjustment?.sequence ?? -1

      const cutoffPA = lastAdjustment
        ? this.livePlateAppearances.find((p) => p.id === lastAdjustment.plateAppearanceId)
        : undefined
      const cutoffPaNumber = cutoffPA?.paNumber ?? -1

      const plateAppearances = this.livePlateAppearances.filter(
        (p) =>
          p.inning === currentPA.inning &&
          p.halfInning === currentPA.halfInning &&
          p.paNumber <= currentPA.paNumber &&
          p.paNumber > cutoffPaNumber, // exclude anything before the jump point
      )
      const plateAppearanceIds = new Set(plateAppearances.map((p) => p.id))
      const lineup =
        (currentPA.halfInning === 'top' ? state.visitingLineup : state.homeLineup) || []

      const txns = this.liveTransactions
        .filter(
          (t) =>
            plateAppearanceIds.has(t.plateAppearanceId) &&
            t.eventType === 'RUNNER_ADVANCE' &&
            t.sequence > cutoffSequence,
        )
        .sort((a, b) => a.sequence - b.sequence) as RunnerAdvanceTransaction[]
      // Map active players and substitutions so runners don't disappear if subbed
      const allPlayers = lineup.concat(lineup.flatMap((l) => l.substitutions || []))
      const playerMap = new Map(allPlayers.map(({ player }) => [player.id, player] as const))

      const runnerFinalBase = new Map<number, string>()
      runnerFinalBase.set(currentPA.batterId, 'B')

      const isBatterOut = this.liveTransactions.some((t) => {
        if (
          t.plateAppearanceId === currentPA.id &&
          (t.eventType === 'PLAY' || t.eventType === 'PITCH')
        ) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const outcome = (t as any).outcome || (t as any).pitchOutcome || ''
          // FIX: Added 'K' and '-K'
          return ['OUT', 'OUT_Q', 'DP', 'TP', 'K', '-K'].includes(outcome)
        }
        return false
      })

      if (isBatterOut) runnerFinalBase.set(currentPA.batterId, 'OUT')

      for (const t of txns) {
        if (t.isOut) runnerFinalBase.set(t.runnerId, 'OUT')
        else if (t.runsScored > 0) runnerFinalBase.set(t.runnerId, 'SCORED')
        else runnerFinalBase.set(t.runnerId, t.endBase)
      }

      for (const [runnerId, base] of runnerFinalBase) {
        const uniformNumber = playerMap.get(runnerId)?.uniformNumber ?? 0
        if (base === '1' || base === '2' || base === '3' || base === 'B') {
          runnersByBase[base].push({ id: runnerId, uniformNumber })
        }
      }
      return runnersByBase
    },
    currentGameTransactions(): GameTransaction[] {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return []
      return this.liveTransactions.filter((t) => t.plateAppearanceId === currentPA.id)
    },
    currentAtBatTransactions(): PitchTransaction[] {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return []
      return this.liveTransactions.filter(
        (t) => t.plateAppearanceId === currentPA.id && t.eventType === 'PITCH',
      ) as PitchTransaction[]
    },
    currentRunnerTransactions(state) {
      const currentPA = state.plateAppearances[state.currentPAIndex]
      if (!currentPA) return []

      const txns = this.liveTransactions.filter(
        (t) => t.plateAppearanceId === currentPA.id && t.eventType === 'RUNNER_ADVANCE',
      ) as RunnerAdvanceTransaction[]
      const lineup =
        (currentPA.halfInning === 'top' ? state.visitingLineup : state.homeLineup) || []

      const allPlayers = lineup.concat(lineup.flatMap((l) => l.substitutions || []))

      return txns.map((txn) => {
        const entry = allPlayers.find((e) => e.player.id === txn.runnerId)
        let meta = ''
        if (txn.isOut) meta += 'Out'
        else meta += txn.advanceReason ?? ''

        if (txn.runsScored !== 0) meta += ' Scored'
        if (txn.playSequence?.length > 0) {
          meta += ` Fld: ${txn.playSequence.map((s) => s.position).join('-')}`
        }
        return {
          ...txn,
          uniformNumber: entry?.player.uniformNumber ?? '-',
          meta: ' ' + meta.trim(),
        }
      })
    },
    getTransactionsByInning(): (inning: number, halfInning: HalfInning) => GameTransaction[] {
      return (inning: number, halfInning: HalfInning) => {
        return this.liveTransactions.filter(
          (t) => t.inning === inning && t.halfInning === halfInning,
        )
      }
    },
    activeTeamCount: (state): number => {
      let count = 0
      if (state.teamHome) count++
      if (state.teamVisiting) count++
      return count
    },
    activeTeamRole: (state): 'home' | 'visiting' | '' => {
      if (!state.activeTeam) return ''
      if (state.activeTeam === state.teamHome) return 'home'
      if (state.activeTeam === state.teamVisiting) return 'visiting'
      return ''
    },
    activeTeamLineup(): LineupEntry[] {
      if (this.activeTeam && this.activeTeam === this.teamHome) return this.homeLineup || []
      if (this.activeTeam && this.activeTeam === this.teamVisiting) return this.visitingLineup || []
      return []
    },
    homeTeamName: (state): string => state.teamHome?.name || 'Home',
    visitingTeamName: (state): string => state.teamVisiting?.name || 'Away',

    currentAbState(): (showRunnerStatus?: boolean) => {
      balls: number
      strikes: number
      status: string | null
    } {
      return (showRunnerStatus: boolean = false) => {
        let b = 0
        let s = 0
        let status: string | null = null

        const currentPA = this.viewedPlateAppearance
        if (!currentPA) return { balls: b, strikes: s, status }

        const txns = this.liveTransactions.filter(
          (t) => t.plateAppearanceId === currentPA.id && t.eventType === 'PITCH',
        ) as PitchTransaction[]
        txns.sort((a, b) => a.sequence - b.sequence)

        for (const p of txns) {
          if (status) break
          const r = p.pitchOutcome
          if (r === 'B' || r === 'W' || r === 'I' || r === 'V' || r === 'P') {
            b++
            if (b >= 4) status = 'BB'
          } else if (r === 'H') {
            status = 'HP'
          } else if (r === 'C' || r === 'S' || r === 'A' || r === 'ST' || r === 'Q' || r === 'D3') {
            s++
            if (s >= 3) {
              if (r === 'D3') {
                status = 'D3'
              } else {
                status = r === 'C' || r === 'A' ? '-K' : 'K'
              }
            }
          } else if (r === 'F' || r === 'T' || r === 'L' || r === 'O' || r === 'R') {
            if (s < 2) s++
          } else if (r === 'X') {
            if (s < 2) s++
            const settingsStore = useSettingsStore()
            const hitOutcomeLabel = settingsStore.allFieldingLabels.find(
              (rule) => rule.value === p.outcome,
            )?.shortCode
            let hitoutcome = hitOutcomeLabel ?? 'Hit'
            let trajectory = p.battedBallTrajectory ? '/' + p.battedBallTrajectory : ''
            const hitclassification = this.getHitClassification(p.hitLocationX, p.hitLocationY)
            trajectory += hitclassification.zone + hitclassification.depth

            const linkedPlay = this.liveTransactions.find(
              (t) => t.eventType === 'PLAY' && String(t.linkedPitchId) === String(p.id),
            ) as PlayTransaction | undefined
            let fieldersStr = ''

            if (linkedPlay && linkedPlay.playSequence && linkedPlay.playSequence.length > 0) {
              if (
                !['O', 'OUT', 'FC'].includes(hitoutcome) &&
                ['O', 'OUT', 'FC', 'DP', 'TP', '1B', '2B', '3B', 'HR'].includes(linkedPlay.outcome)
              ) {
                hitoutcome = linkedPlay.outcome
              }
              fieldersStr = linkedPlay.playSequence
                .map((seq) => {
                  if (seq.isError)
                    return `${seq.position}${seq.errorType === 'throwing' ? 'ET' : 'EF'}`
                  return seq.position.toString()
                })
                .join('-')
              fieldersStr = fieldersStr !== '' ? '.' + fieldersStr : fieldersStr
            }

            // Assign the base hit outcome first
            status = hitoutcome + fieldersStr + trajectory

            // ONLY append the runner statuses if the parameter is true
            if (showRunnerStatus) {
              const runnerTxns = this.liveTransactions.filter(
                (t) =>
                  t.plateAppearanceId === currentPA.id &&
                  t.eventType === 'RUNNER_ADVANCE' &&
                  t.runnerId !== currentPA.batterId &&
                  String(t.linkedPitchId) === String(p.id),
              ) as RunnerAdvanceTransaction[]

              runnerTxns.sort((a, b) => a.sequence - b.sequence)
              const runnerStrings: string[] = []

              for (const rt of runnerTxns) {
                const reason = rt.advanceReason || 'OA'
                const start = rt.startBase === 'B' ? 'H' : rt.startBase
                const end = rt.endBase === 'H' ? 'H' : rt.endBase
                const mid = rt.isOut ? 'X' : '-'
                let fStr = ''
                if (rt.playSequence && rt.playSequence.length > 0) {
                  const mappedFielders = rt.playSequence
                    .map((seq) => {
                      if (seq.isError)
                        return `${seq.position}${seq.errorType === 'throwing' ? 'ET' : 'EF'}`
                      return seq.position.toString()
                    })
                    .join('-')
                  fStr = `(${mappedFielders})`
                }
                runnerStrings.push(`${reason}${start}${mid}${end}${fStr}`)
              }

              if (runnerStrings.length > 0) {
                const runnersCombined = runnerStrings.join(';')
                if (status) status += `.${runnersCombined}`
                else status = runnersCombined
              }
            }
          }
        }
        return { balls: b, strikes: s, status }
      }
    },

    activeViewLineup(state): LineupEntry[] {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return []
      return currentPA.halfInning === 'top' ? state.visitingLineup || [] : state.homeLineup || []
    },
    totalBatters(state): number {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return 9
      const lineup = currentPA.halfInning === 'top' ? state.visitingLineup : state.homeLineup
      if (!Array.isArray(lineup) || lineup.length === 0) return 9

      const settingsStore = useSettingsStore()
      if (settingsStore.useDesignatedHitter) {
        const hasDH = lineup.some((b) => b.startingPosition === 10)
        const pitcher = lineup.find((b) => b.startingPosition === 1)
        // If there's a DH, or the pitcher is explicitly stashed at the bottom of the order (10th+), don't count them as a batter
        if (pitcher && (hasDH || pitcher.battingOrder > 9)) {
          return lineup.length - 1
        }
      }
      return lineup.length
    },
    currentBatter(state): LineupEntry | null {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return null
      const lineup = currentPA.halfInning === 'top' ? state.visitingLineup : state.homeLineup
      if (!Array.isArray(lineup) || lineup.length === 0) return null

      // Find active or substituted player
      let batter = lineup.find(
        (b) => b.player && String(b.player.id) === String(currentPA.batterId),
      )

      if (!batter) {
        batter = lineup
          .flatMap((b) => b.substitutions || [])
          .find((b) => b.player && String(b.player.id) === String(currentPA.batterId))
      }

      // Fallback
      if (!batter) {
        const teamPAs = this.livePlateAppearances.filter(
          (pa) => pa.halfInning === currentPA.halfInning,
        )
        const paIndex = teamPAs.findIndex((pa) => pa.id === currentPA.id)
        if (paIndex !== -1) {
          const orderIndex = (paIndex % lineup.length) + 1
          batter = lineup.find((b) => b.battingOrder === orderIndex)
        } else {
          batter = lineup.find((b) => b.battingOrder === 1) || lineup[0]
        }
      }
      return batter || null
    },
    currentPitcher(state): LineupEntry | null {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return null
      const lineup = currentPA.halfInning === 'top' ? state.homeLineup : state.visitingLineup
      if (!Array.isArray(lineup) || lineup.length === 0) return null

      // 1. Determine the expected pitcher ID from the PA or its defensive snapshot (Position 1)
      const expectedPitcherId = currentPA.pitcherId || currentPA.defenseSnapshot?.[1]?.playerId

      let pitcher = undefined

      if (expectedPitcherId) {
        // Search the starting lineup
        pitcher = lineup.find((b) => b.player && String(b.player.id) === String(expectedPitcherId))

        // Search substitutions if not found in the starting lineup
        if (!pitcher) {
          pitcher = lineup
            .flatMap((b) => b.substitutions || [])
            .find((b) => b.player && String(b.player.id) === String(expectedPitcherId))
        }
      }

      // 2. Ultimate fallback: Use the lineup's assigned starting pitcher, or the first player in the array
      if (!pitcher) {
        pitcher = lineup.find((b) => b.startingPosition === 1) || lineup[0]
      }

      return pitcher || null
    },
    currentPitcherPitchCount(): number {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA || !currentPA.pitcherId) return 0
      return this.liveTransactions.filter(
        (t) => t.eventType === 'PITCH' && String(t.pitcherId) === String(currentPA.pitcherId),
      ).length
    },
    getContinuityResetPoint(): (
      inning: number,
      halfInning: HalfInning,
    ) => { cutoffSequence: number; cutoffPaNumber: number } {
      return (inning: number, halfInning: HalfInning) => {
        // FIX: Use this.liveTransactions
        const inningTxns = this.liveTransactions.filter(
          (t) => t.inning === inning && t.halfInning === halfInning,
        )
        const lastAdjustment = inningTxns
          .filter(
            (t): t is AdjustmentTransaction =>
              t.eventType === 'ADJUSTMENT' && (t.scope === 'inning' || t.scope === 'outs'),
          )
          .sort((a, b) => b.sequence - a.sequence)[0]
        const cutoffSequence = lastAdjustment?.sequence ?? -1

        // FIX: Use this.livePlateAppearances
        const cutoffPA = lastAdjustment
          ? this.livePlateAppearances.find((p) => p.id === lastAdjustment.plateAppearanceId)
          : undefined
        const cutoffPaNumber = cutoffPA?.paNumber ?? -1

        return { cutoffSequence, cutoffPaNumber }
      }
    },

    currentOuts(): number {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return 0
      let outs = 0

      const { cutoffSequence } = this.getContinuityResetPoint(
        currentPA.inning,
        currentPA.halfInning,
      )

      // NEW: Only look at PAs that happened before or during the currently viewed PA
      const validPaIds = new Set(
        this.livePlateAppearances
          .filter(
            (pa) =>
              pa.inning === currentPA.inning &&
              pa.halfInning === currentPA.halfInning &&
              pa.paNumber <= currentPA.paNumber,
          )
          .map((pa) => pa.id),
      )

      // NEW: Filter inningTxns to only include transactions from those valid historical PAs
      const inningTxns = this.liveTransactions.filter(
        (t) =>
          t.inning === currentPA.inning &&
          t.halfInning === currentPA.halfInning &&
          validPaIds.has(t.plateAppearanceId),
      )

      const pitches = inningTxns.filter(
        (t) => t.eventType === 'PITCH' && t.sequence > cutoffSequence,
      ) as PitchTransaction[]
      const plays = inningTxns.filter(
        (t) => t.eventType === 'PLAY' && t.sequence > cutoffSequence,
      ) as PlayTransaction[]
      const advances = inningTxns.filter(
        (t) => t.eventType === 'RUNNER_ADVANCE' && t.sequence > cutoffSequence,
      ) as RunnerAdvanceTransaction[]

      // 3. Batter Outs (Plays and Pitches)
      // Step A: Tally all outs recorded on PLAY transactions (linked or unlinked)
      for (const play of plays) {
        // Safely check for numbers, treating 0 as a valid explicit value
        if (typeof play.outsRecorded === 'number' && play.outsRecorded > 0) {
          outs += play.outsRecorded
        } else {
          // Fallback in case outsRecorded wasn't explicitly set but outcome dictates an out
          if (['OUT', 'OUT_Q', 'FC'].includes(play.outcome)) outs += 1
          if (play.outcome === 'DP') outs += 2
          if (play.outcome === 'TP') outs += 3
        }
      }

      // Step B: Tally outs recorded directly on PITCH transactions (like Strikeouts)
      // ONLY if they don't have a linked play taking precedence
      for (const pitch of pitches) {
        const hasLinkedPlay = plays.some((p) => p.linkedPitchId === pitch.id)

        if (!hasLinkedPlay) {
          // Check the play outcome first, then fall back to the pitch outcome
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const finalOutcome = ((pitch as any).outcome || pitch.pitchOutcome || '') as string
          if (['OUT', 'OUT_Q', 'FC', 'K', '-K'].includes(finalOutcome)) outs += 1
          if (finalOutcome === 'DP') outs += 2
          if (finalOutcome === 'TP') outs += 3
        }
      }

      // 4. Runner Outs (Steals, Pickoffs, Unlinked Events, AND Basepath outs)
      for (const adv of advances) {
        if (adv.isOut) {
          let shouldCount = true

          // Find if this advance is tied to a batted ball play
          const linkedPlay = plays.find(
            (p) =>
              (adv.linkedPlayId && p.id === adv.linkedPlayId) ||
              (adv.linkedPitchId && p.linkedPitchId === adv.linkedPitchId),
          )

          if (linkedPlay) {
            // THE GUARD: If the play is explicitly marked as a DP/TP, OR if the
            // user manually bumped the play's outsRecorded to 2 or 3 in the summary tab,
            // we assume the play transaction is already claiming this runner's out.
            const playOuts = linkedPlay.outsRecorded || 0
            if (['DP', 'TP'].includes(linkedPlay.outcome) || playOuts >= 2) {
              shouldCount = false
            }
          }

          if (shouldCount) {
            outs += 1
          }
        }
      }

      return outs >= 3 ? 3 : outs
    },
  },

  actions: {
    setActiveTeam(team: Team, role: 'home' | 'visiting' = 'home') {
      if (role === 'home') this.teamHome = team
      else this.teamVisiting = team
      this.activeTeam = team
    },

    async changePitcher(newPitcherEntry: LineupEntry) {
      const newPitcherId = newPitcherEntry.player.id
      const currentPA = this.viewedPlateAppearance
      if (!newPitcherId || !this.activeGameSummary || !currentPA) return

      currentPA.pitcherId = newPitcherId
      if (!currentPA.defenseSnapshot) currentPA.defenseSnapshot = {}
      currentPA.defenseSnapshot[1] = { playerId: newPitcherId }

      try {
        await db.plateAppearances.put(JSON.parse(JSON.stringify(currentPA)))
      } catch (e) {
        console.error('Failed to update PA pitcher', e)
      }

      this.activeGameSummary.currentPitcherId = newPitcherId
      const activeDefensiveLineup =
        currentPA.halfInning === 'top' ? this.homeLineup : this.visitingLineup
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const oldPitcher = activeDefensiveLineup!.find(
        (b) => b.startingPosition === 1 && b.player.id !== newPitcherId,
      )
      const newPitcherOldPos = newPitcherEntry.startingPosition

      newPitcherEntry.startingPosition = 1
      if (oldPitcher) oldPitcher.startingPosition = newPitcherOldPos || 13
      await this.updateGameSummary()
    },
    /**
     * Swaps the batter mid-plate-appearance.
     * Applies the new batter to the active lineup slot and pushes the old batter into the substitutions array.
     */
    async changeBatter(newBatterEntry: LineupEntry) {
      if (!this.viewedPlateAppearance || !this.activeGameSummary) return

      // 1. Extract and type-check the new ID!
      const newBatterId = newBatterEntry.player.id
      if (!newBatterId) return // TypeScript now knows newBatterId is strictly a number

      const isTop = this.activeGameSummary.currentHalfInning === 'top'
      const activeOffensiveLineup = isTop ? this.visitingLineup : this.homeLineup

      const currentBatterId = this.viewedPlateAppearance.batterId

      // Find the lineup slot of the current batter
      let slotIndex = activeOffensiveLineup.findIndex(
        (b) => String(b.player.id) === String(currentBatterId),
      )

      // Fallback: If they were somehow already subbed out, find them in the nested arrays
      if (slotIndex === -1) {
        slotIndex = activeOffensiveLineup.findIndex((b) =>
          b.substitutions?.some((sub) => String(sub.player.id) === String(currentBatterId)),
        )
      }

      if (slotIndex !== -1) {
        const currentSlot = activeOffensiveLineup[slotIndex]

        // Tell TypeScript to safely exit if the slot is somehow missing
        if (!currentSlot) return

        // Now TypeScript knows currentSlot is perfectly safe to modify
        if (!currentSlot.substitutions) {
          currentSlot.substitutions = []
        }

        // Deep copy the current entry to safely store in substitutions
        const subbedOut = JSON.parse(JSON.stringify(currentSlot))
        subbedOut.substitutions = [] // Prevent infinite nesting

        currentSlot.substitutions.push(subbedOut)

        // Slot the new player into the top-level active lineup (retaining the battingOrder)
        currentSlot.player = newBatterEntry.player
      }

      // 1. Update the Plate Appearance record to the new batter
      this.viewedPlateAppearance.batterId = newBatterId // Error gone!

      // 2. Update the Game Summary active state
      this.activeGameSummary.currentBatterId = newBatterId // Error gone!

      try {
        await this.updateGameSummary() // Saves the modified lineup array with the new substitution
        await db.plateAppearances.put(JSON.parse(JSON.stringify(this.viewedPlateAppearance)))
      } catch (error) {
        console.error('Failed to save batter change to offline database', error)
      }
    },

    async updateGameSummary() {
      if (!this.activeGameSummary) return
      const gameMetadata = {
        ...this.activeGameSummary,
        teamHome: this.teamHome,
        teamVisiting: this.teamVisiting,
        homeLineup: this.homeLineup,
        visitingLineup: this.visitingLineup,
      }
      await db.games.put(JSON.parse(JSON.stringify(gameMetadata)))
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async logPitch(payload: any) {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA || !this.activeGameSummary) return

      const sequence =
        this.transactions.length > 0 ? Math.max(...this.transactions.map((t) => t.sequence)) + 1 : 1
      const hasLocation = payload.pitchX != null && payload.pitchY != null
      const pitchX = hasLocation ? payload.pitchX : -1
      const pitchY = hasLocation ? payload.pitchY : -1
      const outcome = payload.pitchOutcome ?? payload.outcome ?? payload.result ?? 'B'
      const pitchType = payload.pitchType ?? 'FASTBALL'

      const newPitch: PitchTransaction = {
        id: Date.now().toString(),
        gameId: this.activeGameSummary.gameId,
        timestamp: payload.timestamp || Date.now(),
        sequence,
        plateAppearanceId: currentPA ? currentPA.id : 'null',
        context: 'SCORER',
        inning: currentPA.inning,
        halfInning: currentPA.halfInning,
        batterId: currentPA.batterId,
        pitcherId: currentPA.pitcherId,
        isIncomplete: false,
        notes: null,
        activeTransactionFlag: true,
        eventType: 'PITCH',
        pitchOutcome: outcome,
        pitchType,
        pitchX,
        pitchY,
        locationTracked: hasLocation,
      }

      this.transactions.push(newPitch)

      try {
        await db.transactions.put(JSON.parse(JSON.stringify(newPitch)))
      } catch (err) {
        console.error('Failed to save pitch to offline storage:', err)
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async updatePitch(id: string | number, payload: any) {
      const pitchIndex = this.transactions.findIndex((p) => p.id === String(id))

      if (pitchIndex !== -1) {
        const updates: Partial<PitchTransaction> = { ...payload }
        if (payload.relX !== undefined) updates.pitchX = payload.relX
        if (payload.relY !== undefined) updates.pitchY = payload.relY
        if (payload.result !== undefined) updates.pitchOutcome = payload.result

        // ADD THIS LINE: Safely map the terminal outcome
        if (payload.outcome !== undefined) updates.outcome = payload.outcome

        const updatedPitch = { ...this.transactions[pitchIndex], ...updates } as GameTransaction
        this.transactions[pitchIndex] = updatedPitch

        try {
          await db.transactions.put(JSON.parse(JSON.stringify(updatedPitch)))
        } catch (err) {
          console.error('Failed to update pitch in offline storage:', err)
        }
      }
    },

    async deletePitchLinkedTxns(id: string | number) {
      const stringId = String(id)
      const plateAppearanceTxn = this.transactions.find((p) => p.id === stringId)

      const linkedTxns = this.transactions.filter((t) => {
        if (t.eventType === 'PLAY' || t.eventType === 'RUNNER_ADVANCE') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          return (t as any).linkedPitchId === stringId
        }
        return false
      })

      const idsToDelete = linkedTxns.map((t) => t.id)
      if (idsToDelete.length === 0) return

      if (this.activeGameSummary && plateAppearanceTxn) {
        let runsToRemove = 0
        linkedTxns.forEach((t) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          runsToRemove += (t as any).runsScored || 0
        })

        if (runsToRemove > 0) {
          const inningIndex = plateAppearanceTxn.inning - 1
          const isHome = plateAppearanceTxn.halfInning === 'bottom'
          const scores = isHome
            ? this.activeGameSummary.homeScoreByInning
            : this.activeGameSummary.visitingScoreByInning
          scores[inningIndex] = Math.max(0, (scores[inningIndex] ?? 0) - runsToRemove)
          await this.updateGameSummary()
        }
      }

      this.transactions = this.transactions.filter((p) => !idsToDelete.includes(p.id))
      try {
        await db.transactions.bulkDelete(idsToDelete)
      } catch (err) {
        console.error(`Failed to delete linked trans to pitch ${stringId}:`, err)
      }
    },

    async deletePitch(id: string | number) {
      await this.deletePitchLinkedTxns(id)
      const stringId = String(id)
      let plateAppearanceIndex = -1
      const plateAppearanceTxn = this.transactions.find((p) => p.id === stringId)
      const idsToDelete = [stringId]

      if (plateAppearanceTxn) {
        const paPitches = this.transactions.filter(
          (t) =>
            t.plateAppearanceId === plateAppearanceTxn.plateAppearanceId && t.eventType === 'PITCH',
        )
        if (paPitches.length <= 1) {
          plateAppearanceIndex = this.plateAppearances.findIndex(
            (p) => p.id === plateAppearanceTxn.plateAppearanceId,
          )
        }
      }

      this.transactions = this.transactions.filter((p) => !idsToDelete.includes(p.id))
      const hasMorePlateAppearances = this.plateAppearances.length > 1

      if (plateAppearanceIndex !== -1 && hasMorePlateAppearances) {
        this.plateAppearances.splice(plateAppearanceIndex, 1)
        if (this.currentPAIndex > 0) this.currentPAIndex--
        else this.currentPAIndex = 0
      }
      try {
        await db.transactions.bulkDelete(idsToDelete)
        if (plateAppearanceTxn && plateAppearanceIndex !== -1 && hasMorePlateAppearances) {
          await db.plateAppearances.delete(plateAppearanceTxn.plateAppearanceId)
        }
      } catch (err) {
        console.error(`Failed to delete pitch ${stringId}:`, err)
      }
    },

    // =========================================================================
    // IN-MEMORY TRANSACTION SEQUENCE BUILDERS
    // =========================================================================
    async getOrCreateActivePlay(): Promise<PlayTransaction | undefined> {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA || !this.activeGameSummary) return undefined

      const pitchTxns = this.currentAtBatTransactions || []
      const activePitchId = this.editingPitchId || pitchTxns[pitchTxns.length - 1]?.id
      if (!activePitchId) return undefined

      let play = this.transactions.find(
        (t) => t.eventType === 'PLAY' && String(t.linkedPitchId) === String(activePitchId),
      ) as PlayTransaction

      if (!play) {
        const sequence =
          this.transactions.length > 0
            ? Math.max(...this.transactions.map((t) => t.sequence)) + 1
            : 1
        play = {
          id: Date.now().toString(),
          gameId: this.activeGameSummary.gameId,
          timestamp: Date.now(),
          sequence,
          plateAppearanceId: currentPA.id,
          context: 'SCORER',
          inning: currentPA.inning,
          halfInning: currentPA.halfInning,
          batterId: currentPA.batterId,
          pitcherId: currentPA.pitcherId,
          isIncomplete: true,
          notes: null,
          activeTransactionFlag: true,
          eventType: 'PLAY',
          outcome: 'OUT',
          linkedPitchId: String(activePitchId),
          playSequence: [],
          runsScored: 0,
          outsRecorded: 0,
        }
        this.transactions.push(play)
        await db.transactions.put(JSON.parse(JSON.stringify(play)))
      }
      return play
    },

    async getOrCreateActiveRunnerAdvance(
      runnerId: number,
      startBase?: Base,
      endBase?: Base,
    ): Promise<RunnerAdvanceTransaction | undefined> {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA || !this.activeGameSummary) return undefined

      // FIX: Grab the active pitch ID so we can link them!
      const pitchTxns = this.currentAtBatTransactions || []
      const activePitchId = this.editingPitchId || pitchTxns[pitchTxns.length - 1]?.id

      // NEW: capture the play's id once, directly — not recomputed later, not inferred
      // from matching pitch ids. This is what makes the "already counted" check stable.
      const linkedPlayId = this.activePlay?.id

      let txn = this.transactions.find(
        (t) =>
          t.eventType === 'RUNNER_ADVANCE' &&
          t.plateAppearanceId === currentPA.id &&
          t.runnerId === runnerId &&
          t.isIncomplete,
      ) as RunnerAdvanceTransaction

      if (!txn) {
        const sequence =
          this.transactions.length > 0
            ? Math.max(...this.transactions.map((t) => t.sequence)) + 1
            : 1
        txn = {
          id: Date.now().toString(),
          gameId: this.activeGameSummary.gameId,
          timestamp: Date.now(),
          sequence,
          plateAppearanceId: currentPA.id,
          context: 'SCORER',
          inning: currentPA.inning,
          halfInning: currentPA.halfInning,
          batterId: currentPA.batterId,
          pitcherId: currentPA.pitcherId,
          isIncomplete: true,
          notes: null,
          activeTransactionFlag: true,
          eventType: 'RUNNER_ADVANCE',
          runnerId,
          startBase: startBase || 'B',
          endBase: endBase || 'B',
          advanceReason: 'OTHER',
          defenseSubReason: null,
          baserunningJump: 'routine',
          baserunningDecision: 'not_required',
          isOut: false,
          runsScored: 0,
          playSequence: [],
          // FIX: Conditionally add the property so strict TypeScript doesn't complain about 'undefined'
          ...(activePitchId ? { linkedPitchId: String(activePitchId) } : {}),
          ...(linkedPlayId ? { linkedPlayId: String(linkedPlayId) } : {}), // NEW
        }
        this.transactions.push(txn)
        await db.transactions.put(JSON.parse(JSON.stringify(txn)))
      }
      return txn
    },

    async addFielderToTransaction(txnId: string, position: number, difficulty: 1 | 2 | 3) {
      const txn = this.transactions.find((t) => t.id === txnId) as
        | PlayTransaction
        | RunnerAdvanceTransaction
      if (!txn) return

      const last = txn.playSequence[txn.playSequence.length - 1]
      if (last && last.position === position) return

      const isFirst = txn.playSequence.length === 0
      txn.playSequence.push({
        id: crypto.randomUUID(), // <--- NEW: Assign a stable, permanent ID
        position,
        isError: false,
        role: isFirst ? 'fielded' : 'received',
        approach: 'routine',
        execution: 'clean',
        transfer: 'not_required',
        throw: 'not_required',
        decision: 'not_required',
        difficulty,
      })
      await db.transactions.put(JSON.parse(JSON.stringify(txn)))
    },

    async updateSequenceItem(txnId: string, index: number, updates: Partial<SequenceItem>) {
      const txn = this.transactions.find((t) => t.id === txnId) as
        | PlayTransaction
        | RunnerAdvanceTransaction
      if (!txn || !txn.playSequence[index]) return
      Object.assign(txn.playSequence[index], updates)
      await db.transactions.put(JSON.parse(JSON.stringify(txn)))
    },

    async removeSequenceItem(txnId: string, index: number) {
      const txn = this.transactions.find((t) => t.id === txnId) as
        | PlayTransaction
        | RunnerAdvanceTransaction
      if (!txn) return
      txn.playSequence.splice(index, 1)
      await db.transactions.put(JSON.parse(JSON.stringify(txn)))
    },

    async reorderSequence(txnId: string, oldIndex: number, newIndex: number) {
      const txn = this.transactions.find((t) => t.id === txnId) as
        | PlayTransaction
        | RunnerAdvanceTransaction
      if (!txn) return
      const item = txn.playSequence.splice(oldIndex, 1)[0]
      if (item) txn.playSequence.splice(newIndex, 0, item)
      await db.transactions.put(JSON.parse(JSON.stringify(txn)))
    },

    async updatePlayOutcome(outcome: PlayOutcome) {
      const play = await this.getOrCreateActivePlay()
      if (!play) return
      play.outcome = outcome
      await db.transactions.put(JSON.parse(JSON.stringify(play)))
    },

    async finalizeCurrentPlay() {
      const play = this.activePlay
      if (!play) return 0

      const outs =
        play.outcome === 'OUT' ? 1 : play.outcome === 'DP' ? 2 : play.outcome === 'TP' ? 3 : 0
      play.outsRecorded = outs
      play.isIncomplete = false

      const fieldersInvolved = play.playSequence.map((s) => s.position)
      let notes = `Play: ${fieldersInvolved.join('-')}.`
      const errors = play.playSequence.filter((s) => s.isError)
      if (errors.length > 0) {
        const errStrs = errors.map((e) => `E${e.position} (${e.errorType})`)
        notes += ` ${errStrs.join(', ')}.`
      }
      play.notes = notes

      await db.transactions.put(JSON.parse(JSON.stringify(play)))
      return outs
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async finalizeRunnerAdvance(txnId: string, payload: any) {
      const txn = this.transactions.find((t) => t.id === txnId) as RunnerAdvanceTransaction
      if (!txn || !this.activeGameSummary) return

      const oldRuns = txn.runsScored || 0
      txn.endBase = payload.endBase ?? txn.endBase
      txn.isOut = payload.isOut ?? txn.isOut
      txn.runsScored = payload.runsScored ?? txn.runsScored
      txn.advanceReason = payload.advanceReason ?? txn.advanceReason
      txn.defenseSubReason = payload.defenseSubReason ?? txn.defenseSubReason
      txn.baserunningJump = payload.baserunningJump ?? txn.baserunningJump
      txn.baserunningDecision = payload.baserunningDecision ?? txn.baserunningDecision
      txn.isIncomplete = false

      const runDiff = txn.runsScored - oldRuns
      if (runDiff !== 0 && this.viewedPlateAppearance) {
        const inningIndex = this.viewedPlateAppearance.inning - 1
        const scores =
          this.viewedPlateAppearance.halfInning === 'top'
            ? this.activeGameSummary.visitingScoreByInning
            : this.activeGameSummary.homeScoreByInning
        scores[inningIndex] = Math.max(0, (scores[inningIndex] ?? 0) + runDiff)
      }

      await db.transactions.put(JSON.parse(JSON.stringify(txn)))
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async logRunnerAdvance(payload: any) {
      if (!this.activeGameSummary || typeof payload.runnerId === 'undefined') return
      const txn = await this.getOrCreateActiveRunnerAdvance(
        payload.runnerId,
        payload.startBase,
        payload.endBase,
      )
      if (!txn) return

      if (payload.playSequence) txn.playSequence = payload.playSequence
      await this.finalizeRunnerAdvance(txn.id, payload)
    },
    async syncBatterAdvanceFromPitch(status: string) {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return

      const batterId = currentPA.batterId
      const settingsStore = useSettingsStore()
      const rule = settingsStore.getPitchRuleForOutcome(status)

      if (rule && rule.endBase) {
        await this.logRunnerAdvance({
          runnerId: batterId,
          startBase: 'B',
          endBase: rule.endBase,
          isOut: rule.isOut,
          runsScored: rule.runsScored,
          advanceReason: status, // Logs 'BB', 'HP', etc.
        })
      }
    },
    async syncBatterAdvanceFromHit(outcome: string) {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return

      const batterId = currentPA.batterId
      const settingsStore = useSettingsStore()
      const rule = settingsStore.getRuleForOutcome(outcome)

      if (rule && rule.endBase) {
        const targetBase = rule.endBase === 'H' ? 'H' : rule.endBase
        await this.logRunnerAdvance({
          runnerId: batterId,
          startBase: 'B',
          endBase: targetBase,
          isOut: false,
          runsScored: targetBase === 'H' ? 1 : 0,
          advanceReason: 'OA',
        })
      } else {
        await this.undoRunnerAdvance(batterId)
      }
    },
    async resetRunnerAdvances() {
      if (!this.activeGameSummary) return
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return

      const existingTxns = this.transactions.filter(
        (t): t is RunnerAdvanceTransaction =>
          t.eventType === 'RUNNER_ADVANCE' && t.plateAppearanceId === currentPA.id,
      )

      if (existingTxns.length > 0) {
        const inningIndex = currentPA.inning - 1
        const scores =
          currentPA.halfInning === 'top'
            ? this.activeGameSummary.visitingScoreByInning
            : this.activeGameSummary.homeScoreByInning
        const runsToRemove = existingTxns.reduce((sum, txn) => sum + (txn.runsScored || 0), 0)
        scores[inningIndex] = (scores[inningIndex] ?? 0) - runsToRemove

        const idsToRemove = existingTxns.map((t) => t.id)
        this.transactions = this.transactions.filter((t) => !idsToRemove.includes(t.id))

        try {
          await db.transactions.bulkDelete(idsToRemove)
        } catch (err) {
          console.error('Failed to reset runner advances:', err)
        }
      }
    },
    async undoRunnerAdvance(runnerId: number) {
      if (!this.activeGameSummary) return
      const plateAppearanceId = this.viewedPlateAppearance ? this.viewedPlateAppearance.id : 'null'
      const existingTxns = this.transactions.filter(
        (t) =>
          t.eventType === 'RUNNER_ADVANCE' &&
          t.plateAppearanceId === plateAppearanceId &&
          t.runnerId === runnerId,
      ) as RunnerAdvanceTransaction[]

      if (existingTxns.length > 0) {
        // Reverse any runs each removed transaction had credited, same as deleteRunnerAdvance
        for (const txn of existingTxns) {
          if (txn.runsScored > 0) {
            const currentPA = this.plateAppearances.find((pa) => pa.id === txn.plateAppearanceId)
            if (currentPA) {
              const inningIndex = currentPA.inning - 1
              const scores =
                currentPA.halfInning === 'top'
                  ? this.activeGameSummary.visitingScoreByInning
                  : this.activeGameSummary.homeScoreByInning
              scores[inningIndex] = Math.max(0, (scores[inningIndex] ?? 0) - txn.runsScored)
            }
          }
        }
        const idsToRemove = existingTxns.map((t) => t.id)
        this.transactions = this.transactions.filter((t) => !idsToRemove.includes(t.id))
        try {
          await db.transactions.bulkDelete(idsToRemove)
        } catch (err) {
          console.error('Failed to undo runner advance:', err)
        }
      }
    },

    async deleteRunnerAdvance(txnId: string) {
      const txnIndex = this.transactions.findIndex((t) => t.id === txnId)
      if (txnIndex !== -1) {
        const txn = this.transactions[txnIndex] as RunnerAdvanceTransaction
        if (txn.runsScored > 0 && this.activeGameSummary) {
          const currentPA = this.plateAppearances.find((pa) => pa.id === txn.plateAppearanceId)
          if (currentPA) {
            const inningIndex = currentPA.inning - 1
            const scores =
              currentPA.halfInning === 'top'
                ? this.activeGameSummary.visitingScoreByInning
                : this.activeGameSummary.homeScoreByInning
            scores[inningIndex] = Math.max(0, (scores[inningIndex] ?? 0) - txn.runsScored)
          }
        }
        this.transactions.splice(txnIndex, 1)
        try {
          await db.transactions.delete(txnId)
        } catch (err) {
          console.error('Failed to delete runner advance:', err)
        }
      }
    },
    async deletePlay(txnId: string) {
      const txnIndex = this.transactions.findIndex((t) => t.id === txnId)
      if (txnIndex !== -1) {
        const txn = this.transactions[txnIndex] as PlayTransaction
        if (txn.runsScored > 0 && this.activeGameSummary) {
          const currentPA = this.plateAppearances.find((pa) => pa.id === txn.plateAppearanceId)
          if (currentPA) {
            const inningIndex = currentPA.inning - 1
            const scores =
              currentPA.halfInning === 'top'
                ? this.activeGameSummary.visitingScoreByInning
                : this.activeGameSummary.homeScoreByInning
            scores[inningIndex] = Math.max(0, (scores[inningIndex] ?? 0) - txn.runsScored)
          }
        }
        this.transactions.splice(txnIndex, 1)
        try {
          await db.transactions.delete(txnId)
        } catch (err) {
          console.error('Failed to delete play:', err)
        }
      }
    },
    /**
     * Safely removes the last fielder from the active play sequence.
     * Synchronizes the removal with the underlying transaction in Dexie.
     */
    async undoLastFielder() {
      const plateAppearanceId = this.viewedPlateAppearance ? this.viewedPlateAppearance.id : 'null'
      if (!plateAppearanceId) return
      const existingPlayTxn = this.transactions.find(
        (t) => t.eventType === 'PLAY' && t.plateAppearanceId === plateAppearanceId,
      ) as PlayTransaction | undefined

      // Safety check: ensure the transaction exists and has a sequence to pop
      if (
        !existingPlayTxn ||
        !existingPlayTxn.playSequence ||
        existingPlayTxn.playSequence.length === 0
      ) {
        return
      }

      // 2. If this is the ONLY fielder in the sequence (or it's already empty), delete the whole transaction
      if (existingPlayTxn.playSequence.length <= 1) {
        // Remove from Pinia state
        this.transactions = this.transactions.filter((t) => t.id !== existingPlayTxn.id)

        // Remove from Dexie
        try {
          await db.transactions.delete(existingPlayTxn.id)
        } catch (err) {
          console.error('Failed to delete empty play transaction in offline storage:', err)
        }
      }
      // 3. Otherwise, just pop the last fielder and update the transaction
      else {
        existingPlayTxn.playSequence.pop()
        try {
          // Deep clone to strip reactive proxies before saving to IndexedDB
          await db.transactions.put(JSON.parse(JSON.stringify(existingPlayTxn)))
        } catch (err) {
          console.error('Failed to update play transaction in offline storage:', err)
        }
      }
    },
    async undoLastRunner() {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return
      await this.undoRunnerAdvance(currentPA.batterId)
    },

    async createPlateAppearance(paNumber: number) {
      if (this.activeGameSummary) {
        const defense = this.currentDefenseSnapshot
        const newPA: PlateAppearance = {
          id: `pa_${this.activeGameSummary.gameId}_${this.activeGameSummary.currentInning}_${this.activeGameSummary.currentHalfInning}_${this.activeGameSummary.currentBatterId}_${paNumber}`,
          gameId: this.activeGameSummary.gameId,
          context: 'SCORER',
          inning: this.activeGameSummary.currentInning,
          halfInning: this.activeGameSummary.currentHalfInning,
          batterId: this.activeGameSummary.currentBatterId || 1,
          pitcherId: this.activeGameSummary.currentPitcherId || 99,
          paNumber: paNumber,
          defenseSnapshot: defense,
        }
        this.plateAppearances.push(newPA)
        try {
          await db.plateAppearances.put(JSON.parse(JSON.stringify(newPA)))
        } catch (err) {
          console.error('Failed to save new PA:', err)
        }
      }
    },
    async createPlateAppearanceDummy(
      gameId: string,
      inning: number,
      halfInning: HalfInning,
      defenseSnapshot: DefenseSnapshot,
    ) {
      const paNumber = this.plateAppearances.length + 1
      const newPA = {
        id: `dummy_pa_${Date.now()}`,
        gameId: gameId,
        context: 'OBSERVER',
        inning: inning,
        halfInning: halfInning,
        paNumber: paNumber,
        defenseSnapshot: { ...defenseSnapshot },
        batterId: null,
        pitcherId: null,
      } as unknown as PlateAppearance
      // Push to the store!
      this.plateAppearances.push(newPA)
      // Keep the store's index pointed at the current context
      this.currentPAIndex = this.plateAppearances.length - 1
      try {
        await db.plateAppearances.put(JSON.parse(JSON.stringify(newPA)))
      } catch (err) {
        console.error('Failed to save new PA:', err)
      }
    },

    resolveBatterAndPitcherForHalf(nextHalfInning: HalfInning): {
      batterId: number
      pitcherId: number
    } {
      const fullLineup = nextHalfInning === 'top' ? this.visitingLineup : this.homeLineup
      let nextBatterId = 1
      let nextPitcherId = 99

      if (fullLineup && fullLineup.length > 0) {
        const settingsStore = useSettingsStore()

        // 1. Isolate the Batting Order (Sort by order)
        let battingLineup = [...fullLineup].sort((a, b) => a.battingOrder - b.battingOrder)

        // NEW: DH Logic - Exclude the Pitcher from the batting order if DH is used
        if (settingsStore.useDesignatedHitter) {
          const hasDH = battingLineup.some((b) => b.startingPosition === 10)
          const pitcher = battingLineup.find((b) => b.startingPosition === 1)

          // If a DH exists OR the Pitcher was explicitly placed in the 10th+ spot, skip their turn to bat
          if (pitcher && (hasDH || pitcher.battingOrder > 9)) {
            battingLineup = battingLineup.filter((b) => b.player.id !== pitcher.player.id)
          }
        }

        // Failsafe in case filtering removes everyone
        if (battingLineup.length === 0) battingLineup = fullLineup

        const teamPAs = this.plateAppearances.filter((pa) => pa.halfInning === nextHalfInning)

        if (teamPAs.length > 0) {
          const lastPA = teamPAs[teamPAs.length - 1]
          const expectedLastIndex = (teamPAs.length - 1) % battingLineup.length
          const expectedSlot = battingLineup[expectedLastIndex]

          let currentIndex = -1
          if (
            expectedSlot &&
            (String(expectedSlot.player.id) === String(lastPA!.batterId) ||
              expectedSlot.substitutions?.some(
                (s) => String(s.player.id) === String(lastPA!.batterId),
              ))
          ) {
            currentIndex = expectedLastIndex
          } else {
            currentIndex = battingLineup.findIndex(
              (b) =>
                String(b.player.id) === String(lastPA!.batterId) ||
                (b.substitutions &&
                  b.substitutions.some((s) => String(s.player.id) === String(lastPA!.batterId))),
            )
          }

          if (currentIndex !== -1) {
            nextBatterId = battingLineup[(currentIndex + 1) % battingLineup.length]?.player?.id || 1
          } else {
            nextBatterId = battingLineup[teamPAs.length % battingLineup.length]?.player?.id || 1
          }
        } else {
          nextBatterId = battingLineup[0]?.player?.id || 1
        }
      }

      // 2. Resolve Pitcher (Unaffected by batting order)
      // Find all previous plate appearances where this team was on defense
      const defensivePAs = this.plateAppearances.filter((pa) => pa.halfInning === nextHalfInning)

      if (defensivePAs.length > 0) {
        // Prioritize the pitcher from the most recent plate appearance
        const lastDefensivePA = defensivePAs[defensivePAs.length - 1]

        const pos1 = lastDefensivePA!.defenseSnapshot?.[1]
        const snapshotPitcherId = typeof pos1 === 'number' ? pos1 : pos1?.playerId

        // Use the PA's pitcherId, fallback to the snapshot, fallback to 99
        nextPitcherId = lastDefensivePA!.pitcherId || snapshotPitcherId || 99
      } else {
        // Fallback to starting lineup if this is their very first time taking the field (1st inning)
        const defensiveLineup = nextHalfInning === 'top' ? this.homeLineup : this.visitingLineup
        if (defensiveLineup && defensiveLineup.length > 0) {
          const pitcherEntry = defensiveLineup.find((b) => b.startingPosition === 1)
          if (pitcherEntry && pitcherEntry.player.id) nextPitcherId = pitcherEntry.player.id
          else nextPitcherId = defensiveLineup[0]?.player?.id || 99
        }
      }

      return { batterId: nextBatterId, pitcherId: nextPitcherId }
    },

    async nextBatter() {
      const currentPA = this.plateAppearances[this.currentPAIndex]
      const isViewingLive = this.currentPAIndex === this.plateAppearances.length - 1

      if (isViewingLive) {
        // dynamically check if the at-bat has reached a terminal outcome
        const finalStatus = this.currentAbState(true).status

        if (!finalStatus) {
          console.warn('Cannot advance past an incomplete at-bat.')
          return
        }
      }

      if (isViewingLive && this.activeGameSummary) {
        let nextInning = this.activeGameSummary.currentInning
        let nextHalfInning = this.activeGameSummary.currentHalfInning
        // NEW: same cutoff logic currentOuts uses — don't count outs from before a jump
        const { cutoffSequence } = this.getContinuityResetPoint(nextInning, nextHalfInning)
        let outs = 0

        // 2. Fetch all transactions for this half inning
        const inningTxns = this.transactions.filter(
          (t) =>
            t.inning === nextInning &&
            t.halfInning === nextHalfInning &&
            t.sequence > cutoffSequence, // NEW
        )
        const plays = inningTxns.filter((t) => t.eventType === 'PLAY')
        const pitches = inningTxns.filter((t) => t.eventType === 'PITCH')
        const advances = inningTxns.filter((t) => t.eventType === 'RUNNER_ADVANCE')

        // 3. Batter Outs (Plays and Pitches)
        // Step A: Tally all outs recorded on PLAY transactions (linked or unlinked)
        for (const play of plays as PlayTransaction[]) {
          outs += play.outsRecorded || 0

          // Fallback in case outsRecorded wasn't explicitly set but outcome dictates an out
          if (!play.outsRecorded) {
            if (play.outcome === 'OUT') outs += 1
            if (play.outcome === 'DP') outs += 2
            if (play.outcome === 'TP') outs += 3
          }
        }

        // Step B: Tally outs recorded directly on PITCH transactions (like Strikeouts)
        // ONLY if they don't have a linked play taking precedence
        for (const pitch of pitches as PitchTransaction[]) {
          const hasLinkedPlay = plays.some((p) => p.linkedPitchId === pitch.id)

          if (!hasLinkedPlay) {
            // FIX: Check the play outcome first, then fall back to the pitch outcome!
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const finalOutcome = ((pitch as any).outcome || pitch.pitchOutcome || '') as string
            if (['OUT', 'OUT_Q', 'FC', 'K', '-K'].includes(finalOutcome)) outs += 1
            if (finalOutcome === 'DP') outs += 2
            if (finalOutcome === 'TP') outs += 3
          }
        }

        // 4. Runner Outs (Steals, Pickoffs, Unlinked Events)
        for (const adv of advances as RunnerAdvanceTransaction[]) {
          if (adv.isOut) {
            let shouldCount = true

            // If the runner advance is linked to a play, check if the play's outcome
            // already absorbs the runner's out (like a Double Play or Triple Play)
            if (adv.linkedPlayId) {
              const linkedPlay = plays.find((p) => p.id === adv.linkedPlayId)
              if (linkedPlay && ['DP', 'TP'].includes(linkedPlay.outcome)) {
                shouldCount = false
              }
            }

            if (shouldCount) {
              outs += 1
            }
          }
        }

        if (outs >= 3) {
          if (nextHalfInning === 'top') nextHalfInning = 'bottom'
          else {
            nextHalfInning = 'top'
            nextInning++
          }
          this.activeGameSummary.currentInning = nextInning
          this.activeGameSummary.currentHalfInning = nextHalfInning
        }

        const { batterId: nextBatterId, pitcherId: nextPitcherId } =
          this.resolveBatterAndPitcherForHalf(nextHalfInning)

        this.activeGameSummary.currentBatterId = nextBatterId
        this.activeGameSummary.currentPitcherId = nextPitcherId

        const nextPaNumber = currentPA ? currentPA.paNumber + 1 : 1
        await this.createPlateAppearance(nextPaNumber)

        try {
          const gameMetadata = {
            ...this.activeGameSummary,
            teamHome: this.teamHome,
            teamVisiting: this.teamVisiting,
            homeLineup: this.homeLineup,
            visitingLineup: this.visitingLineup,
          }
          await db.games.put(JSON.parse(JSON.stringify(gameMetadata)))
        } catch (err) {
          console.error('Failed to save new PA or Game Summary:', err)
        }
      }

      if (this.currentPAIndex < this.plateAppearances.length - 1) this.currentPAIndex++
    },

    previousBatter() {
      if (
        this.currentPAIndex === undefined ||
        !this.plateAppearances ||
        this.plateAppearances.length === 0
      )
        return
      if (this.currentPAIndex > 0) this.currentPAIndex--
      else this.currentPAIndex = 0
    },

    async startNewGame() {
      if (!this.activeGameSummary) return

      // NEW: Use the centralized resolution logic to obey DH rules right from Pitch 1
      const { batterId: firstBatterId, pitcherId: startingPitcherId } =
        this.resolveBatterAndPitcherForHalf('top')

      this.activeGameSummary.currentBatterId = firstBatterId
      this.activeGameSummary.currentPitcherId = startingPitcherId
      this.activeGameSummary.isSetupComplete = true
      this.activeGameSummary.currentHalfInning = 'top'

      const defense: DefenseSnapshot = {}
      this.activeDefensiveLineup.forEach((entry) => {
        const pos = entry.startingPosition as Position
        if (pos >= 1 && pos <= 9 && entry.player.id && !defense[pos])
          defense[pos] = { playerId: entry.player.id }
      })

      const firstPA: PlateAppearance = {
        id: `pa_${this.activeGameSummary.gameId}_1_top_${firstBatterId}`,
        gameId: this.activeGameSummary.gameId,
        context: 'SCORER',
        inning: 1,
        halfInning: 'top',
        batterId: firstBatterId,
        pitcherId: startingPitcherId,
        paNumber: 1,
        defenseSnapshot: defense,
      }

      this.transactions = []
      this.plateAppearances = [firstPA]
      this.currentPAIndex = 0

      const finalMetadata = {
        ...this.activeGameSummary,
        teamHome: this.teamHome,
        teamVisiting: this.teamVisiting,
        homeLineup: this.homeLineup,
        visitingLineup: this.visitingLineup,
      }
      await db.games.put(JSON.parse(JSON.stringify(finalMetadata)))
      await db.plateAppearances.put(JSON.parse(JSON.stringify(firstPA)))
    },

    async applyDefensiveChanges(newSnapshot: DefenseSnapshot, summaryNotes: string) {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA || !this.activeGameSummary) return

      // --- NEW: Sync Pitcher IDs if Position 1 changed ---
      const pos1 = newSnapshot[1]
      const newPitcherId = typeof pos1 === 'number' ? pos1 : pos1?.playerId
      if (newPitcherId && newPitcherId !== currentPA.pitcherId) {
        currentPA.pitcherId = newPitcherId
        this.activeGameSummary.currentPitcherId = newPitcherId
      }

      // 1. Update the live Plate Appearance defense snapshot
      currentPA.defenseSnapshot = { ...newSnapshot }

      // 2. Generate a single, clean transaction for the DB
      const sequence =
        this.transactions.length > 0 ? Math.max(...this.transactions.map((t) => t.sequence)) + 1 : 1

      const adminTxn: AdminTransaction = {
        id: Date.now().toString(),
        gameId: this.activeGameSummary.gameId,
        timestamp: Date.now(),
        sequence,
        plateAppearanceId: currentPA.id,
        context: 'SCORER',
        inning: currentPA.inning,
        halfInning: currentPA.halfInning,
        batterId: currentPA.batterId,
        pitcherId: currentPA.pitcherId, // Now accurately reflects the new pitcher
        isIncomplete: false,
        notes: summaryNotes,
        activeTransactionFlag: true,
        eventType: 'ADMIN',
        outcome: 'NP',
        defence: { ...newSnapshot },
      }

      this.transactions.push(adminTxn)

      try {
        await db.transactions.put(JSON.parse(JSON.stringify(adminTxn)))
        await db.plateAppearances.put(JSON.parse(JSON.stringify(currentPA)))
        // Update the game summary to ensure the DB knows about the new currentPitcherId
        await this.updateGameSummary()
      } catch (err) {
        console.error('Failed to save batch defensive changes:', err)
      }
    },

    async swapDefensivePlayer(position: number, newPlayerId: number) {
      const summary = this.activeGameSummary
      const currentPA = this.viewedPlateAppearance
      if (!summary || !currentPA) return

      const sequence =
        this.transactions.length > 0 ? Math.max(...this.transactions.map((t) => t.sequence)) + 1 : 1

      const updatedDefense = { ...currentPA.defenseSnapshot }

      // Scrub the player from their OLD position if they are already on the field
      for (const [posStr, state] of Object.entries(updatedDefense)) {
        const id = typeof state === 'number' ? state : state?.playerId
        if (id === newPlayerId) {
          delete updatedDefense[Number(posStr) as Position]
        }
      }

      // Assign them to the NEW position
      updatedDefense[position as Position] = { playerId: newPlayerId }

      // --- NEW: Sync Pitcher IDs if Position 1 changed ---
      if (position === 1 && currentPA.pitcherId !== newPlayerId) {
        currentPA.pitcherId = newPlayerId
        summary.currentPitcherId = newPlayerId
      }

      const adminTxn: AdminTransaction = {
        id: Date.now().toString(),
        gameId: summary.gameId,
        timestamp: Date.now(),
        sequence,
        plateAppearanceId: currentPA.id,
        context: 'SCORER',
        inning: currentPA.inning,
        halfInning: currentPA.halfInning,
        batterId: currentPA.batterId,
        pitcherId: currentPA.pitcherId, // Now accurately reflects the new pitcher
        isIncomplete: false,
        notes: `Subbed Player ID ${newPlayerId} at Position ${position}`,
        activeTransactionFlag: true,
        eventType: 'ADMIN',
        outcome: 'NP',
        defence: updatedDefense,
      }
      this.transactions.push(adminTxn)
      currentPA.defenseSnapshot = updatedDefense

      try {
        await db.transactions.put(JSON.parse(JSON.stringify(adminTxn)))
        await db.plateAppearances.put(JSON.parse(JSON.stringify(currentPA)))
        // Update the game summary in the DB
        await this.updateGameSummary()
      } catch (err) {
        console.error('Failed to save admin transaction:', err)
      }
    },
    async updateFielderAlignment(position: number, relX: number, relY: number) {
      const summary = this.activeGameSummary
      const currentPA = this.viewedPlateAppearance
      if (!summary || !currentPA || !currentPA.defenseSnapshot) return

      const updatedDefense = { ...currentPA.defenseSnapshot }
      const currentState = updatedDefense[position as Position]
      const playerId = typeof currentState === 'number' ? currentState : currentState?.playerId

      if (!playerId) return

      // Inject the custom coordinates
      updatedDefense[position as Position] = { playerId, relX, relY }

      const sequence =
        this.transactions.length > 0 ? Math.max(...this.transactions.map((t) => t.sequence)) + 1 : 1

      const adminTxn: AdminTransaction = {
        id: Date.now().toString(),
        gameId: summary.gameId,
        timestamp: Date.now(),
        sequence,
        plateAppearanceId: currentPA.id,
        context: this.appContext,
        inning: currentPA.inning,
        halfInning: currentPA.halfInning,
        batterId: currentPA.batterId,
        pitcherId: currentPA.pitcherId,
        isIncomplete: false,
        notes: `Shifted Position ${position}`,
        activeTransactionFlag: true,
        eventType: 'ADMIN',
        outcome: 'NP',
        defence: updatedDefense, // Saves the shift
      }

      this.transactions.push(adminTxn)
      currentPA.defenseSnapshot = updatedDefense

      try {
        await db.transactions.put(JSON.parse(JSON.stringify(adminTxn)))
        await db.plateAppearances.put(JSON.parse(JSON.stringify(currentPA)))
      } catch (err) {
        console.error('Failed to save fielder shift:', err)
      }
    },

    async loadGameFromStorage(gameId: string) {
      try {
        const gameMeta = await db.games.get(gameId)
        const gamePAs = await db.plateAppearances.where('gameId').equals(gameId).toArray()
        const gameTxns = await db.transactions.where('gameId').equals(gameId).sortBy('sequence')

        if (!gameMeta) throw new Error('Game not found')

        this.teamHome = gameMeta.teamHome
        this.teamVisiting = gameMeta.teamVisiting
        this.homeLineup = gameMeta.homeLineup
        this.visitingLineup = gameMeta.visitingLineup
        this.editingLineupEntry = null

        this.activeGameSummary = { ...gameMeta }
        this.plateAppearances = gamePAs
        this.transactions = gameTxns
        this.currentPAIndex =
          this.plateAppearances.length > 0 ? this.plateAppearances.length - 1 : 0
        return { status: 'success' }
      } catch (error) {
        return { status: 'error', message: error }
      }
    },

    setEditingBatter(entry: LineupEntry) {
      this.editingLineupEntry = JSON.parse(JSON.stringify(entry))
    },
    clearEditingBatter() {
      this.editingLineupEntry = null
    },
    moveBatter(role: 'home' | 'visiting', index: number, direction: 'up' | 'down') {
      const lineup = role === 'home' ? this.homeLineup : this.visitingLineup
      if (!lineup) return
      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (targetIndex >= 0 && targetIndex < lineup.length) {
        const temp = lineup[index]!
        lineup[index] = lineup[targetIndex]!
        lineup[targetIndex] = temp
        lineup.forEach((entry, i) => (entry.battingOrder = i + 1))
      }
    },
    async updateCurrentBatterDetails(uniformNumber: number, bats: 'L' | 'R' | 'S') {
      const currentPA = this.viewedPlateAppearance
      if (!currentPA) return

      // Determine which team is currently batting
      const isTop = currentPA.halfInning === 'top'
      const lineup = isTop ? this.visitingLineup : this.homeLineup
      const activeTeam = isTop ? this.teamVisiting : this.teamHome

      const entry = lineup?.find((e) => String(e.player.id) === String(currentPA.batterId))
      if (!entry) return

      // 1. Generate the readable Player Code
      const teamName = activeTeam?.name || 'Unknown'
      const safeTeamName = teamName.replace(/\s+/g, '_')

      // Grab the first character. If it's empty or a space, replace it with an underscore.
      const firstChar =
        entry.player.name && entry.player.name.length > 0
          ? entry.player.name.charAt(0).replace(/\s/g, '_')
          : '_'

      const newPlayerCode = `${safeTeamName}_${uniformNumber}_${firstChar}`

      // 2. Update the active lineup entry
      entry.player.uniformNumber = uniformNumber
      entry.player.bats = bats
      entry.player.playerCode = newPlayerCode

      // 3. Save the active game (syncs the lineup to the games table)
      await this.saveActiveGame()

      // 4. Sync the changes directly to the Master Team Roster in Dexie
      if (activeTeam && activeTeam.id) {
        const rosterPlayer = activeTeam.players.find(
          (p) => String(p.id) === String(currentPA.batterId),
        )

        if (rosterPlayer) {
          rosterPlayer.uniformNumber = uniformNumber
          rosterPlayer.bats = bats
          rosterPlayer.playerCode = newPlayerCode

          try {
            // Write the fully updated team object back to the IndexedDB teams table
            await db.teams.put(JSON.parse(JSON.stringify(activeTeam)))
          } catch (err) {
            console.error('Failed to sync updated batter to master team roster:', err)
          }
        }
      }
    },

    async removeBatterFromLineup(role: 'home' | 'visiting', index: number) {
      if (role === 'home' && this.homeLineup) this.homeLineup.splice(index, 1)
      else if (this.visitingLineup) this.visitingLineup.splice(index, 1)
      await this.saveActiveGame()
    },
    async saveActiveGame() {
      if (this.activeGameSummary) {
        const gameMetadata = {
          ...this.activeGameSummary,
          homeLineup: this.homeLineup,
          visitingLineup: this.visitingLineup,
        }
        await db.games.put(JSON.parse(JSON.stringify(gameMetadata)))
      }
    },
    reverseLineup(role: 'home' | 'visiting') {
      const lineup = role === 'home' ? this.homeLineup : this.visitingLineup
      if (!lineup || lineup.length === 0) return
      lineup.reverse()
      lineup.forEach((entry, i) => (entry.battingOrder = i + 1))
    },

    async saveBatter(role: 'home' | 'visiting', entry: LineupEntry) {
      const lineup = role === 'home' ? this.homeLineup : this.visitingLineup
      if (!lineup) return
      const existingIndex = lineup.findIndex((b) => b.battingOrder === entry.battingOrder)
      if (existingIndex !== -1 && this.editingLineupEntry) lineup[existingIndex] = entry
      else lineup.push(entry)

      lineup.sort((a, b) => a.battingOrder - b.battingOrder)
      this.clearEditingBatter()
      await this.saveActiveGame()
    },

    clearActiveTeams() {
      this.teamHome = null
      this.teamVisiting = null
      this.activeTeam = null
      this.homeLineup = []
      this.visitingLineup = []
      this.editingLineupEntry = null
      this.activeGameSummary = null
      this.transactions = []
      this.plateAppearances = []
      this.currentPAIndex = 0
    },
    async deleteGame(gameId: string) {
      try {
        await db.games.delete(gameId)
        await db.plateAppearances.where('gameId').equals(gameId).delete()
        await db.transactions.where('gameId').equals(gameId).delete()
        if (this.activeGameSummary?.gameId === gameId) this.clearActiveTeams()
      } catch (e) {
        console.error('Failed to delete game from offline storage:', e)
      }
    },
    async populateTeamFromLineup() {
      const team = this.activeTeam
      if (!team) return
      const lineup = this.activeTeamLineup || []

      // Extract valid IDs and names from the lineup (including subs) so we can save them all
      const allLineupEntries = lineup.concat(lineup.flatMap((l) => l.substitutions || []))

      // Grab the safe team name for the player code prefix
      const safeTeamName = (team.name || 'Unknown').replace(/\s+/g, '_')

      // Update existing players or append new ones
      for (const entry of allLineupEntries) {
        const alreadyExistsIndex = team.players.findIndex(
          (p) => (p.id && p.id === entry.player.id) || (p.name && p.name === entry.player.name),
        )

        // 1. Generate the Player Code
        const uniformNumber = entry.player.uniformNumber ?? 0
        const name = entry.player.name || ' '
        const firstChar = name.trim().length > 0 ? name.trim().charAt(0).replace(/\s/g, '_') : '_'
        const playerCode = `${safeTeamName}_${uniformNumber}_${firstChar}`

        // 2. Stamp the code onto the active lineup entry
        entry.player.playerCode = playerCode

        if (alreadyExistsIndex === -1) {
          // It's a new player! Add them to the master roster.
          team.players.push({
            ...entry.player,
            uniformNumber: uniformNumber,
            primaryPosition: entry.startingPosition ?? 13,
            primaryBattingOrder: entry.battingOrder,
            playerCode: playerCode,
          })
        } else {
          // Player exists. Update their baseline stats to match what was just used in the lineup.
          const player = team.players[alreadyExistsIndex]
          if (player) {
            player.uniformNumber = entry.player.uniformNumber ?? player.uniformNumber ?? 0
            player.primaryPosition = entry.startingPosition ?? player.primaryPosition
            player.playerCode = playerCode

            // Only update their primary batting order if they are a starter (not a sub)
            const isSub = lineup.some((l) => l.substitutions?.includes(entry))
            if (!isSub) {
              player.primaryBattingOrder = entry.battingOrder
            }
          }
        }
      }

      try {
        // Save the updated master roster
        await db.teams.put(JSON.parse(JSON.stringify(team)))
        // Save the active game so the lineup entries retain their new playerCodes
        await this.saveActiveGame()
      } catch (error) {
        console.error('Failed to update team roster:', error)
      }
    },
    async exportObserverData(
      gameId: string,
      options: { syncToCloud: boolean; downloadJson: boolean; coachInitials?: string },
    ) {
      try {
        const authStore = useAuthStore()
        const userSettings = authStore.currentUser?.settings as UserSettings | undefined

        // 1. Fetch local records
        const observerTxns = (await db.transactions
          .where('gameId')
          .equals(gameId)
          .filter((t) => t.context === 'OBSERVER')
          .toArray()) as ScoutingTransaction[]

        const observerPAs = await db.plateAppearances
          .where('gameId')
          .equals(gameId)
          .filter((pa) => pa.context === 'OBSERVER')
          .toArray()

        if (observerTxns.length === 0 && observerPAs.length === 0) {
          throw new Error('No observer data found for this game.')
        }

        const scoutingSettings = {
          customLabels: userSettings?.customLabels || {},
          customCategories: userSettings?.customCategories || {},
          hiddenScoutingTags: userSettings?.hiddenScoutingTags || [],
        }

        // 2. SUPABASE CLOUD SYNC
        if (options.syncToCloud) {
          // Upsert the current local settings as the master game settings
          const { error: settingsError } = await supabase.from('demo_settings').upsert(
            {
              game_id: gameId,
              coach_initials: options.coachInitials || 'UNK', // <--- ADDED
              custom_labels: scoutingSettings.customLabels,
              custom_categories: scoutingSettings.customCategories,
              hidden_scouting_tags: scoutingSettings.hiddenScoutingTags,
              updated_at: new Date().toISOString(),
            },
            { onConflict: 'game_id' },
          )

          if (settingsError) throw new Error(`Settings Sync Error: ${settingsError.message}`)

          // Bulk insert all local observations as a cloud backup
          if (observerTxns.length > 0) {
            const supabasePayload = observerTxns.map((txn) => ({
              game_id: gameId,
              coach_initials: options.coachInitials || 'UNK',
              player_ids: txn.targetPlayerIds || [],
              tag_value: txn.tagValue,
              category: txn.tagCategory,
              note: txn.customNote || null,
              snapshot_settings: scoutingSettings,
            }))

            const { error: obsError } = await supabase
              .from('demo_observations')
              .insert(supabasePayload)

            if (obsError) throw new Error(`Observations Sync Error: ${obsError.message}`)
          }
        }

        // 3. OPTIONAL JSON DOWNLOAD
        if (options.downloadJson) {
          const exportPayload = {
            exportType: 'OBSERVER_MERGE',
            gameId: gameId,
            transactions: observerTxns,
            plateAppearances: observerPAs,
            scoutingSettings: scoutingSettings,
          }

          const jsonContent = JSON.stringify(exportPayload, null, 2)
          const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' })
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute(
            'download',
            `observer_sync_${gameId}_${new Date().toISOString().split('T')[0]}.json`,
          )
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      } catch (err) {
        console.error('Failed to export observer data:', err)
        throw err // Re-throw so the UI can catch it and display a notification
      }
    },
    async exportDatabaseToJSON(
      tables: string[] = ['teams', 'games', 'transactions', 'plateAppearances', 'users'],
      filenamePrefix = 'pitchtracker_backup',
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const consolidatedData: Record<string, any> = {}

      for (const tableName of tables) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const table = (db as any)[tableName]
        if (!table) continue
        const records = await table.toArray()
        if (records.length === 0) continue
        consolidatedData[tableName] = records
      }

      const jsonContent = JSON.stringify(consolidatedData, null, 2)
      const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.json`,
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    async importDatabaseFromJSON(
      jsonString: string,
      tables: string[] = ['teams', 'games', 'transactions', 'plateAppearances', 'users'],
    ) {
      if (!jsonString) return
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let parsedData: Record<string, any> = {}
      try {
        parsedData = JSON.parse(jsonString)
      } catch {
        throw new Error('Invalid JSON backup file.')
      }

      const isObserverMerge = parsedData.exportType === 'OBSERVER_MERGE'

      if (isObserverMerge) {
        // --- OBSERVER MERGE LOGIC (Append-Only) ---
        const tablesToMerge = ['transactions', 'plateAppearances']

        for (const tableName of tablesToMerge) {
          const records = parsedData[tableName]
          if (records && Array.isArray(records) && records.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const targetTable = (db as any)[tableName]
            if (targetTable) {
              // bulkPut safely inserts new records and updates existing ones without clearing the table
              await targetTable.bulkPut(records)
            }
          }
        }

        // Live UI Sync: If the imported data belongs to the game currently being scored,
        // push the new observer records straight into Pinia so they appear immediately.
        if (this.activeGameSummary && this.activeGameSummary.gameId === parsedData.gameId) {
          if (parsedData.plateAppearances) {
            const existingPaIds = new Set(this.plateAppearances.map((pa) => pa.id))
            const newPAs = parsedData.plateAppearances.filter(
              (pa: PlateAppearance) => !existingPaIds.has(pa.id),
            )
            this.plateAppearances.push(...newPAs)
          }
          if (parsedData.transactions) {
            const existingTxnIds = new Set(this.transactions.map((t) => t.id))
            const newTxns = parsedData.transactions.filter(
              (t: GameTransaction) => !existingTxnIds.has(t.id),
            )
            this.transactions.push(...newTxns)
          }
        }
      } else {
        // --- STANDARD SCORER LOGIC (Destructive Overwrite) ---
        for (const tableName of tables) {
          const records = parsedData[tableName]
          if (records && Array.isArray(records)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const targetTable = (db as any)[tableName]
            if (targetTable) {
              await targetTable.clear()
              await targetTable.bulkAdd(records)
            }
          }
        }

        const touchesActiveGameState = tables.some((t) =>
          ['teams', 'games', 'transactions', 'plateAppearances'].includes(t),
        )
        if (touchesActiveGameState) {
          this.clearActiveTeams()
        }
      }
    },
    /**
     * Calculates how close a hit is to a fielder's starting position.
     * Operates purely on relative coordinate math, making it usable anywhere in the app.
     * * Returns:
     * 1 = Within 50% of zone radius (Routine / Center)
     * 2 = Between 50% and 100% of zone radius (Tough / Ranging)
     * 3 = Outside position zone radius (Missed / Web Gem)
     */
    calculateHitProximity(
      position: number,
      hitRelX: number,
      hitRelY: number,
      fielderRelX: number,
      fielderRelY: number,
    ): 1 | 2 | 3 {
      const settingsStore = useSettingsStore()

      // 1. Determine the pure relative radius for the given position
      let baseRadius = 0.15
      switch (position) {
        case 7:
        case 8:
        case 9:
          baseRadius = settingsStore.fielderZoneScale[7] || 0.25
          break
        case 4:
        case 6:
          baseRadius = settingsStore.fielderZoneScale[6] || 0.16
          break
        case 3:
        case 5:
          baseRadius = settingsStore.fielderZoneScale[5] || 0.12
          break
        case 1:
          baseRadius = settingsStore.fielderZoneScale[1] || 0.04
          break
        case 2:
          baseRadius = settingsStore.fielderZoneScale[2] || 0.08
          break
      }

      // 2. Calculate the exact literal distance between the hit and the fielder (Pythagorean theorem)
      const dist = Math.sqrt(
        Math.pow(hitRelX - fielderRelX, 2) + Math.pow(hitRelY - fielderRelY, 2),
      )

      // 3. Categorize the distance
      if (dist <= baseRadius * 0.5) return 1 // Center cut / Routine
      if (dist <= baseRadius) return 2 // Outer edge / Tough
      return 3 // Outside zone
    },

    generateGameNarrative(): PlateAppearanceNarrative[] {
      const settingsStore = useSettingsStore()
      const narratives: PlateAppearanceNarrative[] = []

      const getPitchOutcomeLabel = (val: string) => {
        for (const cats of Object.values(settingsStore.subPitchCategories)) {
          const found = cats.find((c) => c.value === val)
          if (found) return found.label
        }
        const primary = settingsStore.primaryPitchCategories.find((c) => c.value === val)
        if (primary) return primary.label
        if (val === 'X') return 'In Play'
        const adv = settingsStore.pitchAdvancedOptions.find((c) => c.value === val)
        if (adv) return adv.label
        return val
      }

      const getRunnerReasonLabel = (val: string) => {
        const primary = settingsStore.primaryRunnerAdvanceReasonsSimplified.find(
          (c) => c.value === val,
        )
        if (primary) return primary.label
        return val
      }

      const pas = [...this.plateAppearances].sort((a, b) => a.paNumber - b.paNumber)

      for (const pa of pas) {
        const offLineup = pa.halfInning === 'top' ? this.visitingLineup : this.homeLineup
        const defLineup = pa.halfInning === 'top' ? this.homeLineup : this.visitingLineup

        // Map active AND subbed players for historical narrative matching
        const allOff = offLineup
          ? offLineup.concat(offLineup.flatMap((l) => l.substitutions || []))
          : []
        const allDef = defLineup
          ? defLineup.concat(defLineup.flatMap((l) => l.substitutions || []))
          : []

        const batterEntry = allOff.find((b) => String(b.player.id) === String(pa.batterId))
        const batterName = batterEntry
          ? `#${batterEntry.player.uniformNumber || '-'} ${batterEntry.player.name}`
          : `Batter ${pa.batterId}`

        const pitcherEntry = allDef.find((p) => String(p.player.id) === String(pa.pitcherId))
        const pitcherName = pitcherEntry
          ? `#${pitcherEntry.player.uniformNumber || '-'} ${pitcherEntry.player.name}`
          : `Pitcher ${pa.pitcherId}`

        const inningText = `${pa.halfInning === 'top' ? 'Top' : 'Bottom'} ${pa.inning}`
        const paTxns = this.transactions
          .filter((t) => t.plateAppearanceId === pa.id)
          .sort((a, b) => a.sequence - b.sequence)

        const pitches: string[] = []
        let play = ''
        const runners: string[] = []

        for (const t of paTxns) {
          if (t.eventType === 'PITCH') {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const p = t as PitchTransaction
            const pType =
              settingsStore.pitchTypes.find((pt) => pt.value === p.pitchType)?.label || p.pitchType
            const pRes = getPitchOutcomeLabel(p.pitchOutcome)
            pitches.push(`${pType} (${pRes})`)
          } else if (t.eventType === 'PLAY') {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const p = t as PlayTransaction
            const outLabel =
              settingsStore.allFieldingLabels.find((l) => l.value === p.outcome)?.label || p.outcome
            let fStr = ''
            if (p.playSequence && p.playSequence.length > 0) {
              const fSeq = p.playSequence
                .map((seq) => {
                  const posStr = settingsStore.positionStrings[seq.position] || String(seq.position)
                  return seq.isError ? `${posStr} (Error)` : posStr
                })
                .join(' to ')
              fStr = ` [${fSeq}]`
            }
            play = `${outLabel}${fStr}`
          } else if (t.eventType === 'RUNNER_ADVANCE') {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            const r = t as RunnerAdvanceTransaction
            const rEntry = allOff.find((b) => String(b.player.id) === String(r.runnerId))
            const rName = rEntry ? `Runner #${rEntry.player.uniformNumber || '-'}` : `Runner`
            const reason = getRunnerReasonLabel(r.advanceReason)
            const dest = r.endBase === 'H' ? 'Home' : `${r.endBase} Base`

            let fStr = ''
            if (r.playSequence && r.playSequence.length > 0) {
              const fSeq = r.playSequence
                .map((seq) => {
                  const posStr = settingsStore.positionStrings[seq.position] || String(seq.position)
                  return seq.isError ? `${posStr}(E)` : posStr
                })
                .join('-')
              fStr = ` [Fielded by: ${fSeq}]`
            }

            if (r.isOut) runners.push(`${rName} was out at ${dest} (${reason})${fStr}`)
            else if (r.runsScored > 0) runners.push(`${rName} scored (${reason})${fStr}`)
            else runners.push(`${rName} advanced to ${dest} (${reason})${fStr}`)
          }
        }

        let summary = `${inningText}: ${batterName} vs ${pitcherName}. `
        if (pitches.length > 0)
          summary += `${pitches.length} pitch${pitches.length > 1 ? 'es' : ''} (${pitches.join(', ')}). `
        if (play) summary += `Result: ${play}. `
        if (runners.length > 0) summary += runners.join('. ') + '.'

        narratives.push({
          paId: pa.id,
          paNumber: pa.paNumber,
          inning: inningText,
          batter: batterName,
          pitcher: pitcherName,
          pitches,
          play,
          runners,
          summary: summary.trim(),
        })
      }
      return narratives
    },
    // Adjustment transaction
    async adjustScore(halfInning: HalfInning, inningIndex: number, delta: number, reason?: string) {
      if (!this.activeGameSummary) return
      const scores =
        halfInning === 'top'
          ? this.activeGameSummary.visitingScoreByInning
          : this.activeGameSummary.homeScoreByInning
      const previousValue = scores[inningIndex] ?? 0
      scores[inningIndex] = Math.max(0, previousValue + delta)

      await this.logAdjustment('score', previousValue, scores[inningIndex], reason)
      await this.updateGameSummary()
    },

    async jumpToInning(inning: number, halfInning: HalfInning, reason?: string) {
      if (!this.activeGameSummary) return
      const previousValue = `${this.activeGameSummary.currentHalfInning} ${this.activeGameSummary.currentInning}`
      this.activeGameSummary.currentInning = inning
      this.activeGameSummary.currentHalfInning = halfInning

      // NEW: resolve the correct batter/pitcher for the inning being jumped to,
      // same rotation logic nextBatter uses — don't leave stale pre-jump values in place.
      const { batterId, pitcherId } = this.resolveBatterAndPitcherForHalf(halfInning)
      this.activeGameSummary.currentBatterId = batterId
      this.activeGameSummary.currentPitcherId = pitcherId

      await this.logAdjustment('inning', previousValue, `${halfInning} ${inning}`, reason)
      const currentPA = this.plateAppearances[this.currentPAIndex]
      const nextPaNumber = currentPA ? currentPA.paNumber + 1 : 1
      await this.createPlateAppearance(nextPaNumber)
      if (this.currentPAIndex < this.plateAppearances.length - 1) this.currentPAIndex++
      await this.updateGameSummary()
    },

    async jumpToLineupSpot(batterId: number, reason?: string) {
      if (!this.activeGameSummary) return
      const previousValue = this.activeGameSummary.currentBatterId ?? 0

      if (previousValue !== batterId) {
        this.activeGameSummary.currentBatterId = batterId

        const currentPA = this.plateAppearances[this.currentPAIndex]

        // 1. Check if the current PA has actual game events attached (Pitches, Plays, Baserunning)
        const hasGameEvents = this.transactions.some(
          (t) =>
            t.plateAppearanceId === currentPA?.id &&
            ['PITCH', 'PLAY', 'RUNNER_ADVANCE'].includes(t.eventType),
        )

        // Log the adjustment so we still have a paper trail of the jump
        await this.logAdjustment('lineup_spot', previousValue, batterId, reason)

        // 2. Only create a NEW plate appearance if the current one has already been acted upon
        if (hasGameEvents) {
          const nextPaNumber = currentPA ? currentPA.paNumber + 1 : 1
          await this.createPlateAppearance(nextPaNumber)
          if (this.currentPAIndex < this.plateAppearances.length - 1) this.currentPAIndex++
        } else if (currentPA) {
          // 3. Otherwise, recycle the empty PA and silently update it to the new batter
          currentPA.batterId = batterId
          try {
            await db.plateAppearances.put(JSON.parse(JSON.stringify(currentPA)))
          } catch (err) {
            console.error('Failed to update PA batter ID in offline storage:', err)
          }
        }
      }
      await this.updateGameSummary()
    },
    async logAdjustment(
      scope: AdjustmentScope,
      previousValue: string | number,
      newValue: string | number,
      reason?: string,
    ) {
      if (!this.activeGameSummary) return
      const currentPA = this.viewedPlateAppearance
      const sequence =
        this.transactions.length > 0 ? Math.max(...this.transactions.map((t) => t.sequence)) + 1 : 1

      const txn: AdjustmentTransaction = {
        id: Date.now().toString(),
        gameId: this.activeGameSummary.gameId,
        context: 'SCORER',
        timestamp: Date.now(),
        sequence,
        plateAppearanceId: currentPA?.id || 'null',
        inning: this.activeGameSummary.currentInning,
        halfInning: this.activeGameSummary.currentHalfInning,
        batterId: this.activeGameSummary.currentBatterId,
        pitcherId: this.activeGameSummary.currentPitcherId,
        isIncomplete: false,
        notes: reason || null,
        activeTransactionFlag: true,
        eventType: 'ADJUSTMENT',
        scope,
        previousValue,
        newValue,
      }
      this.transactions.push(txn)
      try {
        await db.transactions.put(JSON.parse(JSON.stringify(txn)))
      } catch (err) {
        console.error('Failed to save adjustment:', err)
      }
    },

    exportGameToCSV() {
      if (!this.activeGameSummary) return

      const gameId = this.activeGameSummary.gameId
      const settingsStore = useSettingsStore()

      // --- HELPER: MASTER PLAYER DICTIONARY ---
      // We build a map of player IDs to their analytical playerCodes
      const playerMap = new Map<number, string>()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const addPlayers = (lineup: any[]) => {
        if (!lineup) return
        lineup.forEach(entry => {
          if (entry.player.id) {
            playerMap.set(entry.player.id, entry.player.playerCode || `Unknown_${entry.player.uniformNumber || 0}_X`)
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          entry.substitutions?.forEach((sub: any) => {
            if (sub.player.id) {
              playerMap.set(sub.player.id, sub.player.playerCode || `Unknown_${sub.player.uniformNumber || 0}_X`)
            }
          })
        })
      }

      addPlayers(this.homeLineup || [])
      addPlayers(this.visitingLineup || [])

      const getCode = (id: number | undefined | null) => id ? (playerMap.get(id) || String(id)) : ''

      // --- HELPER: LABEL TRANSLATOR ---
      // Translates raw database codes into human-readable user settings labels using dynamic getters
      const getLabel = (
        category: string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: any,
        pos: number = 0,
        role: string = 'fielded',
        traj: string = ''
      ): string => {
        if (value === null || value === undefined || value === '') return ''
        const valStr = String(value)

        let opts: { label: string; value: string | number }[] = []

        try {
          switch (category) {
            case 'role':
              opts = settingsStore.primaryFielderRoles || []
              break
            case 'approach':
              if (pos && typeof settingsStore.approachOptionsForPosition === 'function') {
                opts = settingsStore.approachOptionsForPosition(pos, role, traj)
              }
              break
            case 'execution':
              if (pos && typeof settingsStore.executionOptionsForPosition === 'function') {
                opts = settingsStore.executionOptionsForPosition(pos, role, traj)
              }
              break
            case 'throw':
              if (pos && typeof settingsStore.throwOptionsForPosition === 'function') {
                opts = settingsStore.throwOptionsForPosition(pos, role)
              }
              break
            case 'decision':
              if (pos && typeof settingsStore.decisionOptionsForPosition === 'function') {
                opts = settingsStore.decisionOptionsForPosition(pos, role)
              }
              break
            case 'errorType':
              opts = settingsStore.primaryPlayErrorStates || []
              break
            case 'trajectory':
              opts = settingsStore.trajectoryOptions || []
              break
            case 'advanceReason':
              opts = settingsStore.primaryRunnerAdvanceReasonsSimplified || []
              break
            case 'outcome':
              opts = [
                ...(settingsStore.playAllOutcomes || []),
                ...(settingsStore.primaryPitchCategories || []),
                ...(settingsStore.pitchAdvancedOptions || []),
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(Object.values(settingsStore.subPitchCategories || {}).flat() as any[] || [])
              ]
              break
            case 'pitchType':
              opts = settingsStore.pitchTypes || []
              break
          }
        } catch (e) {
          console.warn(`Label lookup failed for ${category}`, e)
        }

        const found = opts.find((o) => String(o.value) === valStr)
        return found ? found.label : valStr
      }

      // --- HELPER: CSV ESCAPE STRINGS ---
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const escapeCsv = (val: any): string => {
        const str = val === null || val === undefined ? '' : String(val)
        if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`
        return str
      }

      // --- CSV HEADERS ---
      const headers = [
        // Base Context
        'Game_ID', 'Inning', 'Half', 'PA_ID', 'PA_Pitcher_Code', 'PA_Batter_Code',
        // Transaction Context
        'Event_Type', 'Txn_ID', 'Timestamp', 'Txn_Sequence',
        // Pitch Data (17 columns)
        'Pitch_ID', 'Pitch_Sequence', 'Pitch_Timestamp', 'Pitch_Pitcher_Code', 'Pitch_Type', 'Pitch_Outcome', 'Location_Tracked', 'Pitch_X', 'Pitch_Y', 'Pitch_Quality', 'Pitch_Batter_Code', 'Hit_Location_X', 'Hit_Location_Y', 'Hit_Quality', 'Batted_Ball_Trajectory', 'Zone_Fielder_Position', 'Terminal_Outcome',
        // Play & Runner Data (12 columns)
        'Runner_Code', 'Start_Base', 'End_Base', 'Is_Out', 'Play_Runs_Scored', 'Play_Outs_Recorded', 'Play_Quality', 'Linked_Pitch_ID', 'Advance_Reason', 'Defense_Sub_Reason', 'Baserunning_Jump', 'Baserunning_Decision',
        // Sequence Specifics (10 columns)
        'Seq_Index', 'Seq_Position', 'Seq_Is_Error', 'Seq_Error_Type', 'Seq_Role', 'Seq_Approach', 'Seq_Execution', 'Seq_Throw', 'Seq_Decision', 'Seq_Difficulty'
      ]

      // Add Defensive Snapshot Columns Dynamically (27 columns)
      for (let pos = 1; pos <= 9; pos++) {
        headers.push(`Def_${pos}_Player`, `Def_${pos}_RelX`, `Def_${pos}_RelY`)
      }

      const rows: string[][] = [headers]
      const pas = [...this.plateAppearances].sort((a, b) => a.paNumber - b.paNumber)

      // --- BUILD ROWS ---
      for (const pa of pas) {
        const half = pa.halfInning === 'top' ? 'Top' : 'Bottom'
        const baseContext = [
          gameId, pa.inning, half, pa.id, getCode(pa.pitcherId), getCode(pa.batterId)
        ]

        // Capture Defensive Snapshot for this PA
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const defCols: any[] = []
        for (let pos = 1; pos <= 9; pos++) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const snap = (pa.defenseSnapshot as any)?.[pos]
          defCols.push(
            getCode(snap?.playerId),
            snap?.relX ?? '',
            snap?.relY ?? ''
          )
        }

        const paTxns = this.transactions
          .filter((t) => t.plateAppearanceId === pa.id)
          .sort((a, b) => a.sequence - b.sequence)

        // Helper for padding missing data blocks
        const emptyCols = (count: number) => Array(count).fill('')

        for (const t of paTxns) {
          const txnContext = [
            t.eventType, t.id, t.timestamp, t.sequence
          ]

          if (t.eventType === 'PITCH') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const p = t as any
            const pitchCols = [
              p.id, p.sequence, p.timestamp, getCode(p.pitcherId),
              getLabel('pitchType', p.pitchType),
              getLabel('outcome', p.pitchOutcome),
              p.locationTracked, p.pitchX, p.pitchY, p.qualityRating,
              getCode(p.batterId), p.hitLocationX, p.hitLocationY, p.hitQualityRating,
              getLabel('trajectory', p.battedBallTrajectory),
              p.zoneFielderPosition,
              getLabel('outcome', p.outcome || '')
            ]

            rows.push([...baseContext, ...txnContext, ...pitchCols, ...emptyCols(12), ...emptyCols(10), ...defCols].map(escapeCsv))

          } else if (t.eventType === 'PLAY') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const p = t as any
            const playCols = [
              '', '', '', '', p.runsScored, p.outsRecorded, p.qualityRating, p.linkedPitchId, '', '', '', ''
            ]

            // Find trajectory of the batted ball from the linked pitch to dictate approach options (Grounder vs Flyball)
            let traj = ''
            if (p.linkedPitchId) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const linkedPitch = this.transactions.find(tx => tx.id === p.linkedPitchId) as any
              if (linkedPitch) traj = linkedPitch.battedBallTrajectory || ''
            }

            // Unfurl Sequence Array to multiple rows (or 1 empty row if no sequence)
            const seqs = p.playSequence?.length ? p.playSequence : [null]
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            seqs.forEach((seq: any, idx: number) => {
              const seqCols = seq ? [
                idx,
                seq.position,
                seq.isError,
                getLabel('errorType', seq.errorType),
                getLabel('role', seq.role),
                getLabel('approach', seq.approach, seq.position, seq.role, traj),
                getLabel('execution', seq.execution, seq.position, seq.role, traj),
                getLabel('throw', seq.throw, seq.position, seq.role),
                getLabel('decision', seq.decision, seq.position, seq.role),
                seq.difficulty
              ] : emptyCols(10)

              rows.push([...baseContext, ...txnContext, ...emptyCols(17), ...playCols, ...seqCols, ...defCols].map(escapeCsv))
            })

          } else if (t.eventType === 'RUNNER_ADVANCE') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const r = t as any
            const runnerCols = [
              getCode(r.runnerId), r.startBase, r.endBase, r.isOut, r.runsScored, '', '', '',
              getLabel('advanceReason', r.advanceReason),
              r.defenseSubReason,
              r.baserunningJump,
              r.baserunningDecision
            ]

            let traj = ''
            if (r.linkedPitchId) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const linkedPitch = this.transactions.find(tx => tx.id === r.linkedPitchId) as any
              if (linkedPitch) traj = linkedPitch.battedBallTrajectory || ''
            }

            // Unfurl Sequence Array to multiple rows
            const seqs = r.playSequence?.length ? r.playSequence : [null]
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            seqs.forEach((seq: any, idx: number) => {
              const seqCols = seq ? [
                idx,
                seq.position,
                seq.isError,
                getLabel('errorType', seq.errorType),
                getLabel('role', seq.role),
                getLabel('approach', seq.approach, seq.position, seq.role, traj),
                getLabel('execution', seq.execution, seq.position, seq.role, traj),
                getLabel('throw', seq.throw, seq.position, seq.role),
                getLabel('decision', seq.decision, seq.position, seq.role),
                seq.difficulty
              ] : emptyCols(10)

              rows.push([...baseContext, ...txnContext, ...emptyCols(17), ...runnerCols, ...seqCols, ...defCols].map(escapeCsv))
            })

          } else {
            // ADJUSTMENT or SCOUTING Events
            rows.push([...baseContext, ...txnContext, ...emptyCols(17), ...emptyCols(12), ...emptyCols(10), ...defCols].map(escapeCsv))
          }
        }
      }

      // --- GENERATE AND DOWNLOAD BLOB ---
      const csvContent = rows.map((r) => r.join(',')).join('\r\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `pitchtracker_analytical_${this.activeGameSummary.gameId}_${new Date().toISOString().split('T')[0]}.csv`,
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  },
})
